/**
 * Admin panel store — writes directly to SQL tables.
 * No localStorage, no flexfit_catalog JSON, no schedulePush.
 *
 * Data flow: Admin actions → module state → SQL tables.
 * On startup: loadAllFromSQL() → module state → React components.
 */

import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import type {
  Vendor,
  VendorWithStats,
  VendorApplication,
  ApplicationStatus,
  PayoutRequest,
  PayoutStatus,
  Boutique,
  VendorStatus,
} from "@/types";

// ─── Event bus ────────────────────────────────────────────────────────────────

const STORE_UPDATE_EVENT = "flexfit-admin-store-update";

function notifyStoreUpdate() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(STORE_UPDATE_EVENT));
  }
}

export function subscribeStoreUpdate(listener: () => void) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(STORE_UPDATE_EVENT, listener);
  return () => window.removeEventListener(STORE_UPDATE_EVENT, listener);
}

// ─── Module-level state ────────────────────────────────────────────────────────

let _products: Product[] = [];
let _categories: Category[] = [];
let _config: StoreConfig;
let _combos: ComboBundle[] = [];
let _productRequests: ProductRequest[] = [];
let _realOrders: RealOrder[] = [];
let _categoryAffinities: CategoryAffinityRow[] = [];
let _productAffinities: ProductAffinityRow[] = [];
let _groupCartTiers: GroupCartTierRow[] = [];
let _dailyStats: DailyStatRow[] = [];
let _vendors: Vendor[] = [];
let _vendorApplications: VendorApplication[] = [];
let _boutiques: Boutique[] = [];
let _payoutRequests: PayoutRequest[] = [];
let _cloudHydrated = false;
// Promesse du chargement en cours : déduplique les appels concurrents à
// loadAllFromSQL (LoginPage précharge pendant que ProtectedRoute attend).
let _loadPromise: Promise<void> | null = null;

// ─── Load error flag ──────────────────────────────────────────────────────────
// Les fetchers ci-dessous avalent leurs erreurs et renvoient [] : sans ce flag,
// une panne réseau est indistinguable d'une donnée réellement vide dans les
// pages admin (qui affichaient « Aucune donnée » au lieu d'un état erreur).

let _lastLoadError: string | null = null;

/** Dernier scope de chargement en échec, null si le dernier chargement est clean. */
export function getLastLoadError(): string | null {
  return _lastLoadError;
}

function markLoadError(scope: string, err: unknown) {
  _lastLoadError = scope;
  console.error(`[store][SQL] ${scope}:`, err);
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Category {
  id: string;
  label: string;
  slug: string;
  imageSrc?: string;
  icon?: string;
  iconImageSrc?: string;
  parentId: string | null;
  level: number;
  order: number;
}

export interface Product {
  id: string;
  name: string;
  price: number;
  cost?: number;
  weightKg?: number;
  isLightweight?: boolean;
  oldPrice?: number;
  categories: string[];
  sizes: string[];
  colors?: string[];
  searchKeywords?: string[];
  images: string[];
  imageColors?: string[];
  description: string;
  tag?: string;
  trending?: boolean;
  isNew?: boolean;
  cartSuggestionCategoryIds?: string;
  cartSuggestionProductIds?: string[];
  createdAt?: string;
  sku?: string;
  boutiqueId?: string | null;
  isHidden?: boolean;
}

export interface ProductImageSlide {
  url: string;
  colorLabel: string;
  position: number;
}

export interface ProductRequest {
  id: string;
  imageSrc: string;
  message?: string;
  createdAt: string;
  status: "new" | "processed";
}

/** Représente une vraie commande depuis flexfit_user_orders */
export interface RealOrder {
  orderId: string;
  userId: string;
  totalAmount: number;
  itemCount: number;
  itemsSummary: string;
  paymentMethod: string;
  status: string;
  createdAt: string;
  clientName: string;
  clientPhone: string;
  paymentProofUrl?: string | null;
  vendorSplits?: Array<{
    vendorId: string;
    boutiqueId: string | null;
    subtotal: number;
    commissionPercent: number | null;
    commissionAmount: number | null;
    netAmount: number | null;
  }>;
  totalCommission?: number;
  totalNet?: number;
  productImage?: string | null;
}

export interface ComboBundle {
  id: string;
  name: string;
  productIds: string[];
  bundlePrice?: number;
  dealLabel?: string;
  active?: boolean;
}

export interface StoreConfig {
  whatsappNumber: string;
  instagramUrl: string;
  paymentPhone: string;
  airtelMoneyPhone: string;
  orangeMoneyPhone: string;
  mpesaPhone?: string;
  combos?: ComboBundle[];
  bannerImageSrc: string;
  bannerEyebrow: string;
  bannerTitleLine1: string;
  bannerTitle2?: string;
  whatsappOrderMessage?: string;
  faviconUrl?: string;
  appIconUrl?: string;
  navIcon0?: string;
  navIcon1?: string;
  navIcon2?: string;
  navIcon3?: string;
  navIcon4?: string;
  // Delivery countdown
  deliveryCountdownMode?: "date" | "manual";
  nextDeliveryDate?: string;
  deliveryManualHoursRemaining?: number;
  deliveryManualDeadlineAt?: string;
  deliveryNotificationMessage?: string;
  // Admin
  adminEmail?: string;
  contactEmail?: string;
  // Monthly goals
  monthlyProfitGoal?: number;
  monthlyVisitorsGoal?: number;
  // Platform commission
  platformCommissionPercent?: number;
  // Support
  supportTeamName?: string;
  // Hub de regroupement Chine (clés snake_case : persistées telles quelles dans
  // flexfit_store_config.config et lues par le storefront / edge functions)
  hub_address_china?: string;
  hub_deadline_days?: number;
  // Numéro Mobile Money plateforme affiché au checkout (lu par le storefront)
  platform_mobile_money_number?: string;
}

// SQL Row types
export interface CategoryAffinityRow {
  id: string;
  source_category_id: string;
  target_category_ids: string[];
  label: string;
  created_at: string;
  updated_at: string;
}

export interface ProductAffinityRow {
  id: string;
  source_product_id: string;
  target_product_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface GroupCartTierRow {
  id: string;
  min_kg: number;
  max_kg: number;
  price_per_kg: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_CONFIG_VALUE: StoreConfig = {
  whatsappNumber: "",
  instagramUrl: "",
  paymentPhone: "",
  airtelMoneyPhone: "",
  orangeMoneyPhone: "",
  mpesaPhone: "",
  combos: [],
  bannerImageSrc: "",
  bannerEyebrow: "NOUVELLE COLLECTION",
  bannerTitleLine1: "Nouvelle saison,",
  monthlyProfitGoal: 1000,
  monthlyVisitorsGoal: 500,
  platformCommissionPercent: 20,
  supportTeamName: "Équipe Flex & Fit",
  hub_address_china: "",
  hub_deadline_days: 2,
};

export const DEFAULT_CONFIG: StoreConfig = DEFAULT_CONFIG_VALUE;

export function defaultConfig(): StoreConfig {
  return { ...DEFAULT_CONFIG_VALUE };
}

// ─── Product Clicks cache (Supabase data) ─────────────────────────────────────

let _cloudClickCounts: Record<string, number> = {};
let _cloudSalesCounts: Record<string, number> = {};

export function setCloudClickCounts(counts: Record<string, number>) {
  _cloudClickCounts = counts;
}

export function getCloudClickCounts(): Record<string, number> {
  return _cloudClickCounts;
}

export function setCloudSalesCounts(counts: Record<string, number>) {
  _cloudSalesCounts = counts;
}

export function getCloudSalesCounts(): Record<string, number> {
  return _cloudSalesCounts;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function formatPrice(price: number): string {
  return `${price.toLocaleString("fr-FR")} $`;
}

export function slugifyCategoryId(label: string): string {
  return label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
}

export function categoryLabel(categories: Category[], id: string): string {
  return categories.find(c => c.id === id)?.label ?? id;
}

// ─── SQL Row converters ────────────────────────────────────────────────────────

type ProductRow = {
  id: string;
  name: string;
  price: number;
  cost?: number;
  weight_kg?: number;
  is_lightweight?: boolean;
  old_price?: number;
  categories?: string[];
  sizes?: string[];
  colors?: string[];
  search_keywords?: string[];
  images?: string[];
  image_colors?: string[];
  description?: string;
  tag?: string;
  trending?: boolean;
  is_new?: boolean;
  cart_suggestion_category_ids?: string | string[];
  cart_suggestion_product_ids?: string[];
  has_variants?: boolean;
  created_at?: string;
  updated_at?: string;
  sku?: string;
  boutique_id?: string | null;
  is_hidden?: boolean;
};

type CategoryRow = {
  id: string;
  label: string;
  slug: string;
  image_src?: string;
  icon?: string;
  icon_image_src?: string;
  parent_id?: string;
  level?: number;
  sort_order?: never; // intentionally absent — table uses order_index
  order_index?: number;
  created_at?: string;
  updated_at?: string;
};

type ComboRow = {
  id: string;
  label: string;
  product_ids?: string[];
  discount_percent?: number;
  active?: boolean;
  created_at?: string;
  updated_at?: string;
};

function rowToProduct(row: ProductRow): Product {
  return {
    id: row.id,
    name: row.name,
    price: row.price,
    cost: row.cost,
    weightKg: row.weight_kg,
    isLightweight: row.is_lightweight,
    oldPrice: row.old_price,
    categories: Array.isArray(row.categories) ? row.categories : [],
    sizes: Array.isArray(row.sizes) ? row.sizes : [],
    colors: Array.isArray(row.colors) ? row.colors : [],
    searchKeywords: Array.isArray(row.search_keywords) ? row.search_keywords : [],
    images: Array.isArray(row.images) ? row.images : [],
    imageColors: Array.isArray(row.image_colors) ? row.image_colors : [],
    description: row.description ?? "",
    tag: row.tag,
    trending: row.trending,
    isNew: row.is_new,
    cartSuggestionCategoryIds: Array.isArray(row.cart_suggestion_category_ids)
      ? (row.cart_suggestion_category_ids as string[]).join(",")
      : (row.cart_suggestion_category_ids as string | undefined) ?? undefined,
    cartSuggestionProductIds: row.cart_suggestion_product_ids,
    hasVariants: row.has_variants,
    createdAt: row.created_at,
    sku: row.sku,
    boutiqueId: row.boutique_id ?? null,
    isHidden: row.is_hidden ?? false,
  };
}

function productToRow(p: Product): ProductRow {
  return {
    id: p.id,
    name: p.name,
    price: p.price,
    cost: p.cost,
    weight_kg: p.weightKg,
    is_lightweight: p.isLightweight,
    old_price: p.oldPrice,
    categories: Array.isArray(p.categories) ? p.categories : [],
    sizes: Array.isArray(p.sizes) ? p.sizes : [],
    colors: Array.isArray(p.colors) ? p.colors : [],
    search_keywords: Array.isArray(p.searchKeywords) ? p.searchKeywords : [],
    images: Array.isArray(p.images) ? p.images : [],
    image_colors: Array.isArray(p.imageColors) ? p.imageColors : [],
    description: p.description,
    tag: p.tag,
    trending: p.trending ?? false,
    is_new: p.isNew ?? false,
    cart_suggestion_category_ids: Array.isArray(p.cartSuggestionCategoryIds)
      ? p.cartSuggestionCategoryIds
      : typeof p.cartSuggestionCategoryIds === "string"
        ? p.cartSuggestionCategoryIds.split(",").filter(Boolean)
        : [],
    cart_suggestion_product_ids: Array.isArray(p.cartSuggestionProductIds)
      ? p.cartSuggestionProductIds
      : typeof p.cartSuggestionProductIds === "string"
        ? p.cartSuggestionProductIds.split(",").filter(Boolean)
        : [],
    has_variants: p.hasVariants ?? false,
    sku: p.sku,
  };
}

function rowToCategory(row: CategoryRow): Category {
  return {
    id: row.id,
    label: row.label,
    slug: row.slug,
    imageSrc: row.image_src,
    icon: row.icon,
    iconImageSrc: row.icon_image_src,
    parentId: row.parent_id ?? null,
    level: row.level ?? 0,
    order: row.order_index ?? 0,
  };
}

function categoryToRow(c: Category): CategoryRow {
  return {
    id: c.id,
    label: c.label,
    slug: c.slug,
    image_src: c.imageSrc,
    icon: c.icon,
    icon_image_src: c.iconImageSrc,
    parent_id: c.parentId ?? null,
    level: c.level ?? 0,
    order_index: c.order ?? 0,
  };
}

function rowToCombo(row: ComboRow): ComboBundle {
  return {
    id: row.id,
    name: row.label,
    productIds: row.product_ids ?? [],
    bundlePrice: row.discount_percent ? 0 : undefined,
    dealLabel: row.label,
    active: row.active ?? true,
  };
}

function comboToRow(c: ComboBundle): ComboRow {
  return {
    id: c.id,
    label: c.name || c.dealLabel || "",
    product_ids: c.productIds,
    discount_percent: c.bundlePrice ? 0 : 0,
    active: c.active ?? true,
  };
}

// ─── SQL Read functions ────────────────────────────────────────────────────────

export async function fetchProductsFromSQL(): Promise<Product[]> {
  try {
    const { data, error } = await supabase
      .from("flexfit_products")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) { markLoadError("fetchProducts", error); return []; }
    return (data ?? []).map(rowToProduct);
  } catch (err) {
    markLoadError("fetchProducts", err);
    return [];
  }
}

export async function fetchCategoriesFromSQL(): Promise<Category[]> {
  try {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("level", { ascending: true })
      .order("order_index", { ascending: true });
    if (error) { markLoadError("fetchCategories", error); return []; }
    return (data ?? []).map(row => ({
      id: row.id,
      label: row.label,
      slug: row.slug,
      imageSrc: row.image_src,
      icon: row.icon,
      iconImageSrc: row.icon_image_src,
      parentId: row.parent_id ?? null,
      level: row.level ?? 0,
      order: row.order_index ?? 0,
    }));
  } catch (err) {
    markLoadError("fetchCategories", err);
    return [];
  }
}

export async function fetchCombosFromSQL(): Promise<ComboBundle[]> {
  try {
    const { data, error } = await supabase
      .from("flexfit_combos")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) { markLoadError("fetchCombos", error); return []; }
    return (data ?? []).map(rowToCombo);
  } catch (err) {
    markLoadError("fetchCombos", err);
    return [];
  }
}

export async function fetchProductRequestsFromTable(): Promise<ProductRequest[]> {
  try {
    const { data, error } = await supabase
      .from("flexfit_product_requests")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) { markLoadError("fetchProductRequests", error); return []; }
    return (data ?? []).map((row: any) => ({
      id: row.id,
      imageSrc: row.image_src || "",
      message: row.message || "",
      createdAt: row.created_at || new Date().toISOString(),
      status: (row.status === "processed" ? "processed" : "new") as "new" | "processed",
    }));
  } catch (err) { markLoadError("fetchProductRequests", err); return []; }
}

export async function fetchRealOrdersFromSQL(): Promise<RealOrder[]> {
  try {
    const [{ data: userOrders, error: userErr }, { data: guestOrders, error: guestErr }] = await Promise.all([
      supabase.from("flexfit_user_orders").select("*").order("created_at", { ascending: false }),
      supabase.from("flexfit_guest_orders").select("*").order("created_at", { ascending: false }),
    ]);
    if (userErr) markLoadError("fetchRealOrders(user)", userErr);
    if (guestErr) markLoadError("fetchRealOrders(guest)", guestErr);

    const userRows = (userOrders ?? []).map((row: any) => ({
      ...row,
      _source: "user" as const,
      _orderId: row.order_id || row.id || "",
    }));
    const guestRows = (guestOrders ?? []).map((row: any) => ({
      ...row,
      _source: "guest" as const,
      _orderId: row.order_ref || row.id || "",
    }));

    const orders = [...userRows, ...guestRows].sort(
      (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );
    const orderIds = orders.map((row) => row._orderId).filter(Boolean);

    // Fetch payment proofs for these orders
    let proofMap: Record<string, { url?: string; summary?: string; count?: number }> = {};
    if (orderIds.length > 0) {
      const { data: proofs } = await supabase
        .from("flexfit_payment_proofs")
        .select("order_id, image_src, proof_url, items_summary, item_count")
        .in("order_id", orderIds);
      (proofs ?? []).forEach((p: any) => {
        if (p.order_id) {
          proofMap[p.order_id] = {
            url: p.proof_url || p.image_src || null,
            summary: p.items_summary || "",
            count: p.item_count || 0,
          };
        }
      });
    }

    // Fetch first product image per order
    let imageMap: Record<string, string | null> = {};
    const userOrderIds = userRows.map((row) => row._orderId).filter(Boolean);
    const guestOrderIds = guestRows.map((row) => row.id).filter(Boolean);
    console.log("[admin][orders] fetching images for", orderIds.length, "orders, user:", userOrderIds.length, "guest:", guestOrderIds.length);
    if (orderIds.length > 0) {
      const [{ data: userItems }, { data: guestItems }] = await Promise.all([
        userOrderIds.length > 0
          ? supabase.from("order_items").select("order_id, product_snapshot").in("order_id", userOrderIds)
          : Promise.resolve({ data: [] }),
        guestOrderIds.length > 0
          ? supabase.from("flexfit_guest_order_items").select("guest_order_id, product_image").in("guest_order_id", guestOrderIds)
          : Promise.resolve({ data: [] }),
      ]);
      (userItems ?? []).forEach((it: any) => {
        const id = it.order_id;
        if (imageMap[id]) return;
        let snapshot = it.product_snapshot;
        if (typeof snapshot === "string") {
          try { snapshot = JSON.parse(snapshot); } catch { snapshot = null; }
        }
        const url = typeof snapshot === "object" && snapshot ? snapshot.image || snapshot.image_url || snapshot.images?.[0] || null : null;
        if (url) imageMap[id] = url;
      });
      (guestItems ?? []).forEach((it: any) => {
        const id = it.guest_order_id;
        if (imageMap[id]) return;
        if (it.product_image) imageMap[id] = it.product_image;
      });
    }

    // Fetch vendor splits for commission / net breakdown
    let splitMap: Record<string, any[]> = {};
    const guestOrderRefs = guestRows.map((row) => row.order_ref).filter(Boolean);
    let vendorMap = new Map<string, { user_id: string | null; phone: string | null; name: string | null }>();
    let boutiqueMap = new Map<string, string>();

    if (orderIds.length > 0) {
      // Resolve guest order UUIDs from order_refs
      let guestUuidToRef: Record<string, string> = {};
      if (guestOrderRefs.length > 0) {
        const { data: guestOrderUuids } = await supabase
          .from("flexfit_guest_orders")
          .select("id, order_ref")
          .in("order_ref", guestOrderRefs);
        guestUuidToRef = Object.fromEntries(
          (guestOrderUuids ?? []).map((o: any) => [o.id, o.order_ref])
        );
      }

      const [{ data: userSplits }, { data: guestSplits }] = await Promise.all([
        supabase
          .from("flexfit_order_vendor_split")
          .select("user_order_id, vendor_id, boutique_id, subtotal, commission_percent, commission_amount, net_amount")
          .in("user_order_id", orderIds),
        supabase
          .from("flexfit_order_vendor_split")
          .select("guest_order_id, vendor_id, boutique_id, subtotal, commission_percent, commission_amount, net_amount")
          .in("guest_order_id", Object.keys(guestUuidToRef)),
      ]);
      (userSplits ?? []).forEach((s: any) => {
        const key = s.user_order_id;
        if (!splitMap[key]) splitMap[key] = [];
        splitMap[key].push(s);
      });
      (guestSplits ?? []).forEach((s: any) => {
        const key = guestUuidToRef[s.guest_order_id];
        if (!key) return;
        if (!splitMap[key]) splitMap[key] = [];
        splitMap[key].push(s);
      });

      // Bulk-fetch vendors & boutiques for all splits
      const allSplits = [...(userSplits ?? []), ...(guestSplits ?? [])];
      const vendorIds = [...new Set(allSplits.map((s) => s.vendor_id).filter(Boolean))] as string[];
      const boutiqueIds = [...new Set(allSplits.map((s) => s.boutique_id).filter(Boolean))] as string[];

      if (vendorIds.length > 0) {
        const { data: vendors } = await supabase
          .from("vendors")
          .select("id, user_id, phone, name")
          .in("id", vendorIds);
        for (const v of vendors ?? []) {
          vendorMap.set(v.id, { user_id: v.user_id, phone: v.phone, name: v.name });
        }
      }
      if (boutiqueIds.length > 0) {
        const { data: boutiques } = await supabase
          .from("boutiques")
          .select("id, name")
          .in("id", boutiqueIds);
        for (const b of boutiques ?? []) {
          boutiqueMap.set(b.id, b.name);
        }
      }
    }

    return orders.map((row: any) => {
      const id = row._orderId;
      const isGuest = row._source === "guest";
      const proof = proofMap[id] ?? {};
      const splits = splitMap[id] ?? [];
      const totalCommission = splits.reduce((sum: number, s: any) => sum + Number(s.commission_amount ?? 0), 0);
      const totalNet = splits.reduce((sum: number, s: any) => sum + Number(s.net_amount ?? 0), 0);
      return {
        orderId: id,
        userId: isGuest ? "guest" : (row.user_id || ""),
        totalAmount: Number(row.total_amount || 0),
        itemCount: Number(row.item_count || proof.count || 0),
        itemsSummary: row.items_summary || proof.summary || "",
        paymentMethod: row.payment_method || "mobile_money",
        status: row.status || "pending",
        createdAt: row.created_at || new Date().toISOString(),
        clientName: row.client_name || "",
        clientPhone: row.client_phone || "",
        paymentProofUrl: proof.url || null,
        productImage: imageMap[id] || null,
        vendorSplits: splits.map((s: any) => {
          const vendor = vendorMap.get(s.vendor_id);
          return {
            vendorId: s.vendor_id,
            vendorUserId: vendor?.user_id ?? null,
            vendorPhone: vendor?.phone ?? null,
            vendorName: vendor?.name ?? null,
            boutiqueId: s.boutique_id ?? null,
            boutiqueName: s.boutique_id ? boutiqueMap.get(s.boutique_id) ?? null : null,
            subtotal: Number(s.subtotal ?? 0),
            commissionPercent: s.commission_percent ?? null,
            commissionAmount: s.commission_amount ?? null,
            netAmount: s.net_amount ?? null,
          };
        }),
        totalCommission,
        totalNet,
      };
    });
  } catch (err) {
    console.error("[store][SQL] fetchRealOrders exception:", err);
    return [];
  }
}

export async function updateRealOrderStatus(orderId: string, status: string): Promise<void> {
  try {
    const { error: userErr } = await supabase
      .from("flexfit_user_orders")
      .update({ status })
      .eq("order_id", orderId);
    const { error: guestErr } = await supabase
      .from("flexfit_guest_orders")
      .update({ status })
      .eq("order_ref", orderId);
    if (userErr && guestErr) {
      console.error("[store][SQL] updateRealOrderStatus:", userErr, guestErr);
    } else {
      console.log("[store][SQL] Updated order", orderId, "status to", status);
      // Sync product sales to Supabase when order is paid
      if (status === "Payée" || status === "payee") {
        const order = _realOrders.find(o => o.orderId === orderId);
        if (order?.itemsSummary) {
          const products = _products;
          const items = order.itemsSummary.split(",").map(s => s.trim());
          for (const item of items) {
            const productName = item.split(" x")[0].trim().toLowerCase();
            if (!productName) continue;
            const product = products.find(p =>
              p.name.toLowerCase().includes(productName) ||
              productName.includes(p.name.toLowerCase())
            );
            const qtyMatch = item.match(/x(\d+)/);
            const qty = qtyMatch ? parseInt(qtyMatch[1]) : 1;
            if (product) {
              await syncSalesToSupabase(product.id, qty);
              _cloudSalesCounts[product.id] = (_cloudSalesCounts[product.id] ?? 0) + qty;
            }
          }
        }
      }
    }
  } catch (err) {
    console.error("[store][SQL] updateRealOrderStatus exception:", err);
  }
}

export async function confirmOrderPaymentSQL(orderId: string): Promise<boolean> {
  try {
    // 1. Marquer la commande comme payée
    const [{ error: userErr }, { error: guestErr }] = await Promise.all([
      supabase.from("flexfit_user_orders").update({ status: "paid" }).eq("order_id", orderId),
      supabase.from("flexfit_guest_orders").update({ status: "paid" }).eq("order_ref", orderId),
    ]);
    if (userErr && guestErr) {
      console.error("[store][SQL] confirmOrderPayment orders:", userErr, guestErr);
      toast.error("Impossible de confirmer la commande.");
      return false;
    }

    // 2. Récupérer les splits de cette commande
    // Pour les commandes guest, orderId est order_ref ; il faut résoudre l'UUID
    const { data: guestOrder } = await supabase
      .from("flexfit_guest_orders")
      .select("id")
      .eq("order_ref", orderId)
      .maybeSingle();
    const guestOrderUuid = guestOrder?.id;

    const [{ data: userSplits }, { data: guestSplits }] = await Promise.all([
      supabase.from("flexfit_order_vendor_split").select("id").eq("user_order_id", orderId),
      supabase
        .from("flexfit_order_vendor_split")
        .select("id")
        .eq("guest_order_id", guestOrderUuid ?? "00000000-0000-0000-0000-000000000000"),
    ]);

    const splitIds = [
      ...(userSplits ?? []).map((s) => s.id),
      ...(guestSplits ?? []).map((s) => s.id),
    ].filter(Boolean);

    if (splitIds.length === 0) {
      console.warn("[store][SQL] confirmOrderPayment: aucun split trouvé pour", orderId);
      toast.error("Aucun split vendeur trouvé pour cette commande.");
      return false;
    }

    // 3. Valider les splits
    const { error: splitErr } = await supabase
      .from("flexfit_order_vendor_split")
      .update({ status: "paid" })
      .in("id", splitIds);
    if (splitErr) console.error("[store][SQL] confirmOrderPayment splits:", splitErr);

    // 4. Rendre les gains disponibles au retrait (pending, null ou déjà available)
    const { error: payoutErr } = await supabase
      .from("flexfit_payout_records")
      .update({ status: "available" })
      .in("order_vendor_split_id", splitIds)
      .or("status.eq.pending,status.is.null");
    if (payoutErr) {
      console.error("[store][SQL] confirmOrderPayment payouts:", payoutErr);
      toast.error("Erreur lors de la libération des gains.");
      return false;
    }

    await refreshOrdersFromSQL();
    await refreshMarketplaceFromSQL();
    toast.success("Paiement confirmé — les gains sont disponibles pour les vendeurs.");
    return true;
  } catch (err) {
    console.error("[store] confirmOrderPaymentSQL exception:", err);
    toast.error("Erreur lors de la confirmation du paiement.");
    return false;
  }
}

export async function deleteRealOrder(orderId: string): Promise<void> {
  try {
    // Guest orders : splits et items supprimés en CASCADE par la FK.
    // User orders : PAS de cascade — splits et order_items doivent être
    // supprimés explicitement, sinon les vendeurs voient des commandes fantômes.
    const results = await Promise.all([
      supabase.from("flexfit_user_orders").delete().eq("order_id", orderId),
      supabase.from("flexfit_guest_orders").delete().eq("order_ref", orderId),
      supabase.from("flexfit_order_vendor_split").delete().eq("user_order_id", orderId),
      supabase.from("order_items").delete().eq("order_id", orderId),
    ]);
    const hasError = results.every(({ error }) => !!error);
    if (hasError) {
      console.error("[store][SQL] deleteRealOrder:", results[0].error, results[1].error);
      throw results[0].error || results[1].error || new Error("Delete failed");
    }
    _realOrders = _realOrders.filter(o => o.orderId !== orderId);
    notifyStoreUpdate();
  } catch (err) {
    console.error("[store][SQL] deleteRealOrder:", err);
    throw err;
  }
}

export async function createRealOrder(orderData: Omit<RealOrder, "orderId" | "createdAt">): Promise<RealOrder | null> {
  try {
    const orderId = `ORD-${Date.now()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    const newOrder: RealOrder = {
      ...orderData,
      orderId,
      createdAt: new Date().toISOString(),
    };

    const { error } = await supabase
      .from("flexfit_user_orders")
      .insert({
        order_id: orderId,
        user_id: orderData.userId,
        total_amount: orderData.totalAmount,
        item_count: orderData.itemCount,
        items_summary: orderData.itemsSummary,
        payment_method: orderData.paymentMethod,
        status: orderData.status,
        client_name: orderData.clientName,
        client_phone: orderData.clientPhone,
      });

    if (error) {
      console.error("[store][SQL] createRealOrder:", error);
      toast.error("Erreur lors de la création de la commande");
      return null;
    }

    console.log("[store][SQL] Created order", orderId);
    _realOrders = [newOrder, ..._realOrders];
    notifyStoreUpdate();
    toast.success("Commande créée avec succès");
    return newOrder;
  } catch (err) {
    console.error("[store][SQL] createRealOrder exception:", err);
    toast.error("Erreur lors de la création de la commande");
    return null;
  }
}

export async function fetchCategoryAffinitiesFromSQL(): Promise<CategoryAffinityRow[]> {
  try {
    const { data, error } = await supabase
      .from("flexfit_category_affinities")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) { console.warn("[store] fetchCategoryAffinities:", error); return []; }
    return data ?? [];
  } catch (err) {
    console.warn("[store] fetchCategoryAffinities exception:", err);
    return [];
  }
}

export async function fetchProductAffinitiesFromSQL(): Promise<ProductAffinityRow[]> {
  try {
    const { data, error } = await supabase
      .from("flexfit_product_affinities")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) { console.warn("[store] fetchProductAffinities:", error); return []; }
    return data ?? [];
  } catch (err) {
    console.warn("[store] fetchProductAffinities exception:", err);
    return [];
  }
}

export async function fetchGroupCartTiersFromSQL(): Promise<GroupCartTierRow[]> {
  try {
    const { data, error } = await supabase
      .from("flexfit_group_cart_tiers")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) { console.warn("[store] fetchGroupCartTiers:", error); return []; }
    return data ?? [];
  } catch (err) {
    console.warn("[store] fetchGroupCartTiers exception:", err);
    return [];
  }
}

// ─── SQL Write functions ───────────────────────────────────────────────────────

export async function saveCategoryToSQL(category: Category): Promise<void> {
  try {
    const { error } = await supabase
      .from("categories")
      .upsert({
        id: category.id,
        label: category.label,
        slug: category.slug,
        image_src: category.imageSrc ?? null,
        icon: category.icon ?? null,
        icon_image_src: category.iconImageSrc ?? null,
        parent_id: category.parentId ?? null,
        level: category.level ?? 0,
        order_index: category.order ?? 0,
      }, { onConflict: "id" });
    if (error) {
      console.error("[store][SQL] saveCategory:", error);
      throw new Error(error.message);
    }
  } catch (err) {
    console.error("[store][SQL] saveCategory exception:", err);
    throw err;
  }
}

export async function saveProductToSQL(product: Product): Promise<void> {
  _products = (_products ?? []).some(p => p.id === product.id)
    ? _products.map(p => p.id === product.id ? product : p)
    : [..._products, product];
  notifyStoreUpdate();
  try {
    const row = productToRow(product);
    const { error } = await supabase
      .from("flexfit_products")
      .upsert(row, { onConflict: "id" });
    if (error) console.error("[store][SQL] saveProduct:", error);
    else console.log("[store][SQL] Saved product", product.id);
  } catch (err) { console.error("[store][SQL] saveProduct exception:", err); }
}

export async function saveCategoriesToSQL(categories: Category[]): Promise<void> {
  console.log("[store][SQL] saveCategoriesToSQL called with", categories.length, "categories");
  try {
    // Delete removed categories from Supabase
    const { data: existingRows } = await supabase
      .from("categories")
      .select("id")
      .throwOnError();
    const existingIds = new Set((existingRows ?? []).map((r: { id: string }) => r.id));
    const newIds = new Set(categories.map(c => c.id));
    const toDelete = [...existingIds].filter(id => !newIds.has(id));
    if (toDelete.length > 0) {
      console.log("[store][SQL] Deleting from Supabase:", toDelete);
      await supabase.from("categories").delete().in("id", toDelete);
    }

    // Upsert remaining categories
    const rows = categories.map(c => ({
      id: c.id,
      label: c.label,
      slug: c.slug,
      image_src: c.imageSrc ?? null,
      icon: c.icon ?? null,
      icon_image_src: c.iconImageSrc ?? null,
      parent_id: c.parentId ?? null,
      level: c.level ?? 0,
      order_index: c.order ?? 0,
    }));
    console.log("[store][SQL] Upserting rows:", rows.map(r => r.id));
    const { error } = await supabase
      .from("categories")
      .upsert(rows, { onConflict: "id" });
    console.log("[store][SQL] Upsert response:", error ? `ERROR: ${error.message}` : "OK");
    if (error) {
      console.error("[store][SQL] saveCategories ERROR:", JSON.stringify(error, null, 2));
      toast.error(`Erreur SQL: ${error.message}`);
      throw new Error(error.message);
    } else {
      console.log("[store][SQL] Saved", categories.length, "categories to Supabase");
    }
  } catch (err) {
    console.error("[store][SQL] saveCategories exception:", err);
    toast.error("Exception lors de la sauvegarde");
    throw err;
  }
}

export async function pushProductsToSQL(products: Product[]): Promise<void> {
  try {
    const rows = products.map(productToRow);
    const { error } = await supabase
      .from("flexfit_products")
      .upsert(rows, { onConflict: "id" });
    if (error) console.error("[store][SQL] pushProducts:", error);
    else console.log("[store][SQL] Pushed", products.length, "products");
  } catch (err) { console.error("[store][SQL] pushProducts exception:", err); }
}

export async function pushCombosToSQL(combos: ComboBundle[]): Promise<void> {
  try {
    const rows = combos.map(comboToRow);
    const { error } = await supabase
      .from("flexfit_combos")
      .upsert(rows, { onConflict: "id" });
    if (error) console.error("[store][SQL] pushCombos:", error);
    else console.log("[store][SQL] Pushed", combos.length, "combos");
  } catch (err) { console.error("[store][SQL] pushCombos exception:", err); }
}

export async function deleteProductFromSQL(id: string): Promise<void> {
  try {
    await supabase.from("flexfit_products").delete().eq("id", id);
    console.log("[store][SQL] Deleted product:", id);
  } catch (err) { console.error("[store][SQL] deleteProduct exception:", err); }
}

export async function deleteCategoryFromSQL(id: string): Promise<void> {
  try {
    await supabase.from("categories").delete().eq("id", id);
    console.log("[store][SQL] Deleted category:", id);
  } catch (err) { console.error("[store][SQL] deleteCategory exception:", err); }
}

export async function deleteComboFromSQL(id: string): Promise<void> {
  try {
    await supabase.from("flexfit_combos").delete().eq("id", id);
    console.log("[store][SQL] Deleted combo:", id);
  } catch (err) { console.error("[store][SQL] deleteCombo exception:", err); }
}

export async function pushCategoryAffinitiesToSQL(affinities: CategoryAffinityRow[]): Promise<void> {
  try {
    const { error } = await supabase
      .from("flexfit_category_affinities")
      .upsert(affinities.map(a => ({
        id: a.id,
        source_category_id: a.source_category_id,
        target_category_ids: a.target_category_ids,
        label: a.label ?? "",
        created_at: a.created_at,
      })), { onConflict: "id" });
    if (error) console.warn("[store] pushCategoryAffinities:", error);
    else console.log("[store] Pushed", affinities.length, "category affinities");
  } catch (err) { console.warn("[store] pushCategoryAffinities exception:", err); }
}

export async function deleteCategoryAffinityFromSQL(id: string): Promise<void> {
  try {
    await supabase.from("flexfit_category_affinities").delete().eq("id", id);
    console.log("[store] Deleted category affinity:", id);
  } catch (err) { console.warn("[store] deleteCategoryAffinity:", err); }
}

export async function pushProductAffinitiesToSQL(affinities: ProductAffinityRow[]): Promise<void> {
  try {
    const { error } = await supabase
      .from("flexfit_product_affinities")
      .upsert(affinities.map(a => ({
        id: a.id,
        source_product_id: a.source_product_id,
        target_product_ids: a.target_product_ids,
        created_at: a.created_at,
      })), { onConflict: "id" });
    if (error) console.warn("[store] pushProductAffinities:", error);
    else console.log("[store] Pushed", affinities.length, "product affinities");
  } catch (err) { console.warn("[store] pushProductAffinities exception:", err); }
}

export async function deleteProductAffinityFromSQL(id: string): Promise<void> {
  try {
    await supabase.from("flexfit_product_affinities").delete().eq("id", id);
    console.log("[store] Deleted product affinity:", id);
  } catch (err) { console.warn("[store] deleteProductAffinity:", err); }
}

export async function pushGroupCartTiersToSQL(tiers: GroupCartTierRow[]): Promise<void> {
  try {
    const { error } = await supabase
      .from("flexfit_group_cart_tiers")
      .upsert(tiers.map(t => ({
        id: t.id,
        min_kg: t.min_kg,
        max_kg: t.max_kg,
        price_per_kg: t.price_per_kg,
        sort_order: t.sort_order ?? 0,
        created_at: t.created_at,
      })), { onConflict: "id" });
    if (error) console.warn("[store] pushGroupCartTiers:", error);
    else console.log("[store] Pushed", tiers.length, "cart tiers");
  } catch (err) { console.warn("[store] pushGroupCartTiers exception:", err); }
}

export async function deleteGroupCartTierFromSQL(id: string): Promise<void> {
  try {
    await supabase.from("flexfit_group_cart_tiers").delete().eq("id", id);
    console.log("[store] Deleted cart tier:", id);
  } catch (err) { console.warn("[store] deleteGroupCartTier:", err); }
}

// ─── Product Images ───────────────────────────────────────────────────────────

export async function syncProductSlideImages(
  productId: string,
  images: string[],
  imageColors: string[],
): Promise<void> {
  if (!productId) return;
  const isValidUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(productId);
  if (!isValidUuid) {
    await manualSyncProductSlideImages(productId, images, imageColors);
    return;
  }
  try {
    const { error } = await supabase.rpc("sync_product_slide_images", {
      p_product_id: productId,
      p_images: images,
      p_image_colors: imageColors ?? [],
    });
    if (error) {
      console.warn("[store][images] RPC failed:", error.message);
      await manualSyncProductSlideImages(productId, images, imageColors);
    } else {
      console.log("[store][images] Synced", images.length, "images for product", productId);
    }
  } catch (err) {
    console.warn("[store][images] exception:", err);
    await manualSyncProductSlideImages(productId, images, imageColors);
  }
}

async function manualSyncProductSlideImages(
  productId: string,
  images: string[],
  imageColors: string[],
): Promise<void> {
  if (!images || images.length === 0) return;
  await supabase
    .from("flexfit_product_images")
    .delete()
    .eq("product_id", productId);

  const baseRow = {
    product_id: productId,
    url: images[0],
    position: 0,
    color_label: imageColors?.[0] ?? "",
    is_base: true,
    parent_image_id: null as string | null,
  };

  const { data: baseData, error: baseError } = await supabase
    .from("flexfit_product_images")
    .insert(baseRow)
    .select("id")
    .single();

  if (baseError || !baseData) {
    console.error("[store][images] insert base:", baseError);
    return;
  }

  const baseId = baseData.id;
  const slides = [];
  for (let i = 1; i < images.length; i++) {
    slides.push({
      product_id: productId,
      url: images[i],
      position: i,
      color_label: imageColors?.[i] ?? "",
      is_base: false,
      parent_image_id: baseId,
    });
  }
  if (slides.length > 0) {
    await supabase.from("flexfit_product_images").insert(slides);
  }
}

export async function deleteProductImages(productId: string): Promise<void> {
  try {
    await supabase
      .from("flexfit_product_images")
      .delete()
      .eq("product_id", productId);
    console.log("[store][images] Deleted for", productId);
  } catch (err) { console.warn("[store][images] delete:", err); }
}

export async function deleteProductStorageImages(productId: string): Promise<void> {
  try {
    const folderPath = `products/${productId}`;
    const { data: files, error: listError } = await supabase.storage
      .from("product-images")
      .list("", { prefix: folderPath });
    if (listError || !files?.length) return;
    const paths = files.map(f => `${folderPath}/${f.name}`).filter(Boolean);
    await supabase.storage.from("product-images").remove(paths);
    console.log("[store][storage] Deleted", paths.length, "files for", productId);
  } catch (err) { console.warn("[store][storage] delete:", err); }
}

export async function fetchProductSlideImages(productId: string): Promise<ProductImageSlide[]> {
  try {
    const { data, error } = await supabase
      .from("flexfit_product_images")
      .select("url, position, color_label, is_base")
      .eq("product_id", productId)
      .order("position", { ascending: true });
    if (error) { console.warn("[store][images] fetch:", error); return []; }
    return (data ?? [])
      .filter(row => !row.is_base)
      .map(row => ({
        url: row.url,
        colorLabel: row.color_label ?? "",
        position: row.position,
      }));
  } catch { return []; }
}

// ─── Hydration / Load ────────────────────────────────────────────────────────

export async function initializeStore(): Promise<void> {
  await loadAllFromSQL();
}

export async function loadAllFromSQL(force = false): Promise<void> {
  if (!force && _cloudHydrated) return;
  // Déduplique les appels concurrents : sans ça, un prefetch (LoginPage) suivi
  // d'un init (ProtectedRoute) relançait toutes les requêtes SQL en double.
  if (!force && _loadPromise) return _loadPromise;
  const p = loadAllFromSQLInner();
  if (!force) {
    _loadPromise = p;
    const clear = () => { if (_loadPromise === p) _loadPromise = null; };
    p.then(clear, clear);
  }
  await p;
}

async function loadAllFromSQLInner(): Promise<void> {
  _lastLoadError = null;
  console.log("[store] Loading all data from SQL tables...");

  const [
    products, categories, combos,
    requests,
    catAff, prodAff, tiers,
    realOrders,
    vendors, vendorApps, boutiques, payouts,
  ] = await Promise.all([
    fetchProductsFromSQL(),
    fetchCategoriesFromSQL(),
    fetchCombosFromSQL(),
    fetchProductRequestsFromTable(),
    fetchCategoryAffinitiesFromSQL(),
    fetchProductAffinitiesFromSQL(),
    fetchGroupCartTiersFromSQL(),
    fetchRealOrdersFromSQL(),
    fetchVendorsFromSQL(),
    fetchVendorApplicationsFromSQL(),
    fetchBoutiquesFromSQL(),
    fetchPayoutRequestsFromSQL(),
  ]);

  _products = products;
  _categories = categories;
  _combos = combos;
  _productRequests = requests;
  _categoryAffinities = catAff;
  _productAffinities = prodAff;
  _groupCartTiers = tiers;
  _realOrders = realOrders;
  _vendors = vendors;
  _vendorApplications = vendorApps;
  _boutiques = boutiques;
  _payoutRequests = payouts;

  // Try to load config from flexfit_store_config
  try {
    const { data } = await supabase
      .from("flexfit_store_config")
      .select("config")
      .eq("id", "main")
      .maybeSingle();
    if (data?.config) {
      _config = { ...defaultConfig(), ...data.config };
    }
  } catch { /* ignore */ }

  // Load clicks and sales from Supabase
  try {
    const [clicks, sales] = await Promise.all([
      fetchClicksFromSupabase(),
      fetchSalesFromSupabase(),
    ]);
    if (Object.keys(clicks).length > 0) setCloudClickCounts(clicks);
    if (Object.keys(sales).length > 0) setCloudSalesCounts(sales);
  } catch { /* ignore */ }

  _cloudHydrated = true;
  console.log("[store] Loaded:", products.length, "products,", categories.length, "categories,", combos.length, "combos");
  if (payouts.length === 0) {
    console.warn(
      "[store] ⚠️  Table flexfit_payout_records vide ou absente. " +
      "Les requêtes de payout n'apparaîtront pas dans le panel admin."
    );
  }
  notifyStoreUpdate();
}

export async function refreshCatalogFromCloud(): Promise<void> {
  await loadAllFromSQL(true);
}

export async function refreshCatalogFromSQL(): Promise<void> {
  await loadAllFromSQL(true);
}

export async function refreshFullCatalogFromSQL(): Promise<void> {
  await loadAllFromSQL(true);
}

export async function refreshOrdersFromSQL(): Promise<void> {
  // Même logique : un retry réussi doit pouvoir effacer l'état d'erreur orders.
  if (_lastLoadError?.startsWith("fetchRealOrders")) _lastLoadError = null;
  const orders = await fetchRealOrdersFromSQL();
  _realOrders = orders;
  notifyStoreUpdate();
}

// ─── GETTERS ────────────────────────────────────────────────────────────────

export function getProducts(): Product[] { return _products; }
export function getCategories(): Category[] { return _categories; }
export function getConfig(): StoreConfig { return _config; }
export function getCombos(): ComboBundle[] { return _combos; }
export function getProductRequests(): ProductRequest[] { return _productRequests; }
export function getRealOrders(): RealOrder[] { return _realOrders; }
export function getOrderStatsByStatus(): { status: string; count: number; color: string }[] {
  const counts: Record<string, number> = {};
  for (const o of _realOrders) {
    const s = o.status ?? "pending";
    counts[s] = (counts[s] ?? 0) + 1;
  }
  const labels: Record<string, string> = {
    pending: "En attente",
    shipping: "Expédiée",
    payee: "Payée",
    delivered: "Livrée",
    cancelled: "Annulée",
  };
  const colors: Record<string, string> = {
    pending: "#F59E0B",
    shipping: "#06B6D4",
    payee: "#10B981",
    delivered: "#8B5CF6",
    cancelled: "#EF4444",
  };
  return Object.entries(counts).map(([status, count]) => ({
    status,
    count,
    label: labels[status] ?? status,
    color: colors[status] ?? "#71717A",
  }));
}
export function getDailyStats(): DailyStatRow[] { return _dailyStats; }
export function getCategoryAffinities(): CategoryAffinityRow[] { return _categoryAffinities; }
export function getProductAffinities(): ProductAffinityRow[] { return _productAffinities; }
export function getGroupCartTiers(): GroupCartTierRow[] { return _groupCartTiers; }

// ─── SAVES (write to SQL directly) ─────────────────────────────────────────

export async function saveProducts(products: Product[]): Promise<void> {
  _products = products;
  notifyStoreUpdate();
  await pushProductsToSQL(products);
}

export async function saveCategories(categories: Category[]): Promise<void> {
  console.log("[store] saveCategories called with", categories.length, "categories");
  _categories = categories;
  notifyStoreUpdate();
  await saveCategoriesToSQL(categories);
}

export async function saveConfig(config: StoreConfig): Promise<boolean> {
  _config = config;
  notifyStoreUpdate();
  try {
    // Les secrets IA ne doivent JAMAIS atterrir dans le config public
    // (flexfit_store_config a une policy SELECT anon) → table flexfit_admin_secrets.
    const safeConfig = { ...(config as Record<string, unknown>) };
    delete safeConfig.aiApiKey;
    delete safeConfig.aiProvider;
    const { error } = await supabase
      .from("flexfit_store_config")
      .upsert({ id: "main", config: safeConfig }, { onConflict: "id" });
    if (error) throw error;
  } catch (err) {
    console.warn("[store] saveConfig:", err);
    return false;
  }
  // Sync to localStorage so the frontend picks up the changes
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem("flexfit_config", JSON.stringify(config));
    } catch (e) { /* ignore */ }
  }
  return true;
}

export function saveCombos(combos: ComboBundle[]) {
  _combos = combos;
  notifyStoreUpdate();
  void pushCombosToSQL(combos);
}

export function saveCategoryAffinities(affinities: CategoryAffinityRow[]) {
  _categoryAffinities = affinities;
  notifyStoreUpdate();
  void pushCategoryAffinitiesToSQL(affinities);
}

export function saveProductAffinities(affinities: ProductAffinityRow[]) {
  _productAffinities = affinities;
  notifyStoreUpdate();
  void pushProductAffinitiesToSQL(affinities);
}

export function saveGroupCartTiers(tiers: GroupCartTierRow[]) {
  _groupCartTiers = tiers;
  notifyStoreUpdate();
  void pushGroupCartTiersToSQL(tiers);
}

export function saveProductRequests(requests: ProductRequest[]) {
  _productRequests = requests;
  notifyStoreUpdate();
  void supabase.from("flexfit_product_requests").upsert(
    requests.map(r => ({
      id: r.id,
      image_src: r.imageSrc || "",
      message: r.message || "",
      status: r.status,
      created_at: r.createdAt,
    })),
    { onConflict: "id" },
  );
}

/**
 * Supprime réellement une demande produit en base — saveProductRequests ne fait
 * qu'upsert les éléments restants, donc sans DELETE dédié la ligne réapparaît
 * au prochain chargement. Rollback local + toast d'erreur en cas d'échec.
 */
export async function deleteProductRequest(id: string): Promise<boolean> {
  const prev = _productRequests;
  _productRequests = prev.filter((r) => r.id !== id);
  notifyStoreUpdate();
  const { error } = await supabase.from("flexfit_product_requests").delete().eq("id", id);
  if (error) {
    console.error("[store][SQL] deleteProductRequest:", error);
    _productRequests = prev;
    notifyStoreUpdate();
    toast.error("Suppression échouée");
    return false;
  }
  return true;
}

// ─── Analytics SQL ────────────────────────────────────────────────────────────

export interface DailyVisitRow {
  date_key: string;
  visit_count: number;
  updated_at: string;
}

export interface CartEventRow {
  id: string;
  session_id: string;
  user_id: string;
  product_id: string;
  event_type: "view" | "add_to_cart" | "remove_from_cart" | "checkout_started" | "order_completed";
  event_value: number | null;
  created_at: string;
}

export interface ProductAlertRow {
  id: string;
  product_id: string;
  alert_type: string;
  alert_message: string;
  resolved: boolean;
  created_at: string;
  resolved_at: string | null;
}

export interface DailyStatRow {
  date_key: string;
  visits: number;
  cart_adds: number;
  orders: number;
  revenue: number;
  profit: number;
  avg_basket: number;
  updated_at: string;
}

export async function fetchDailyVisitsFromSQL(days = 30): Promise<DailyVisitRow[]> {
  try {
    const { data, error } = await supabase
      .from("flexfit_daily_visits")
      .select("*")
      .order("date_key", { ascending: false })
      .limit(days);
    if (error) { console.warn("[store] fetchDailyVisits:", error); return []; }
    return data ?? [];
  } catch (err) { console.warn("[store] fetchDailyVisits exception:", err); return []; }
}

export async function fetchVisitsTotalFromSQL(): Promise<number> {
  try {
    const { data, error } = await supabase
      .from("flexfit_visits_total")
      .select("total_count")
      .eq("id", "counter")
      .maybeSingle();
    if (error) { console.warn("[store] fetchVisitsTotal:", error); return 0; }
    return data?.total_count ?? 0;
  } catch (err) { console.warn("[store] fetchVisitsTotal exception:", err); return 0; }
}

export async function fetchCartEventsFromSQL(eventType?: string, limit = 200): Promise<CartEventRow[]> {
  try {
    let q = supabase
      .from("flexfit_cart_events")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);
    if (eventType) q = q.eq("event_type", eventType);
    const { data, error } = await q;
    if (error) { console.warn("[store] fetchCartEvents:", error); return []; }
    return data ?? [];
  } catch (err) { console.warn("[store] fetchCartEvents exception:", err); return []; }
}

export async function fetchProductAlertsFromSQL(includeResolved = false): Promise<ProductAlertRow[]> {
  try {
    let q = supabase
      .from("flexfit_product_alerts")
      .select("*")
      .order("created_at", { ascending: false });
    if (!includeResolved) q = q.eq("resolved", false);
    const { data, error } = await q;
    if (error) { console.warn("[store] fetchProductAlerts:", error); return []; }
    return data ?? [];
  } catch (err) { console.warn("[store] fetchProductAlerts exception:", err); return []; }
}

export async function resolveProductAlert(id: string): Promise<void> {
  try {
    await supabase
      .from("flexfit_product_alerts")
      .update({ resolved: true, resolved_at: new Date().toISOString() })
      .eq("id", id);
  } catch (err) { console.warn("[store] resolveProductAlert:", err); }
}

export async function fetchDailyStatsFromSQL(days = 30): Promise<DailyStatRow[]> {
  try {
    const { data, error } = await supabase
      .from("flexfit_daily_stats")
      .select("*")
      .order("date_key", { ascending: false })
      .limit(days);
    if (error) { console.warn("[store] fetchDailyStats:", error); return []; }
    return data ?? [];
  } catch (err) { console.warn("[store] fetchDailyStats exception:", err); return []; }
}

export async function getCartFunnelFromSQL(days = 30): Promise<{
  views: number;
  cartAdds: number;
  checkouts: number;
  orders: number;
}> {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffStr = cutoff.toISOString();
  try {
    const { data, error } = await supabase
      .from("flexfit_cart_events")
      .select("event_type, event_value")
      .gte("created_at", cutoffStr);
    if (error) { console.warn("[store] getCartFunnel:", error); return { views: 0, cartAdds: 0, checkouts: 0, orders: 0 }; }
    let views = 0, cartAdds = 0, checkouts = 0, orders = 0;
    for (const row of (data ?? [])) {
      switch (row.event_type) {
        case "view": views++; break;
        case "add_to_cart": cartAdds++; break;
        case "checkout_started": checkouts++; break;
        case "order_completed": orders++; break;
      }
    }
    return { views, cartAdds, checkouts, orders };
  } catch (err) { console.warn("[store] getCartFunnel exception:", err); return { views: 0, cartAdds: 0, checkouts: 0, orders: 0 }; }
}

export async function refreshAnalyticsFromSQL(): Promise<void> {
  const [dailyVisits, visitsTotal, dailyStats, productAlerts] = await Promise.all([
    fetchDailyVisitsFromSQL(90),
    fetchVisitsTotalFromSQL(),
    fetchDailyStatsFromSQL(90),
    fetchProductAlertsFromSQL(false),
  ]);
  const dailyMap: Record<string, number> = {};
  for (const row of dailyVisits) dailyMap[row.date_key] = row.visit_count;

  sessionStorage.setItem("flexfit_daily_visits", JSON.stringify(dailyMap));
  sessionStorage.setItem("flexfit_visits", String(visitsTotal));
  sessionStorage.setItem("flexfit_daily_stats", JSON.stringify(dailyStats));
  sessionStorage.setItem("flexfit_product_alerts", JSON.stringify(productAlerts));
  _dailyStats = dailyStats;
  console.log("[store] Analytics:", visitsTotal, "visits,", dailyVisits.length, "days,", productAlerts.length, "alerts");
  notifyStoreUpdate();
}

// ─── Stats helpers (SQL data) ─────────────────────────────────────────────────

// Get top products from real orders (based on item count in orders)
export function getTopProductsBySales(): { productId: string; name: string; quantity: number; image?: string }[] {
  const products = _products;
  const orders = _realOrders;

  // Aggregate sales from real orders by parsing itemsSummary
  const salesMap: Record<string, number> = {};
  for (const order of orders) {
    if (!order.itemsSummary) continue;
    // Try to parse items summary and count items
    // Format could be "Veste courte x1, Pantalon x2" or just product names
    const items = order.itemsSummary.split(",").map(s => s.trim());
    for (const item of items) {
      // Extract product name (before "x" if quantity format)
      const productName = item.split(" x")[0].trim().toLowerCase();
      if (!productName) continue;

      // Try to find matching product
      const product = products.find(p => p.name.toLowerCase().includes(productName) || productName.includes(p.name.toLowerCase()));
      if (product) {
        // Extract quantity if present (e.g., "x2")
        const qtyMatch = item.match(/x(\d+)/);
        const qty = qtyMatch ? parseInt(qtyMatch[1]) : 1;
        salesMap[product.id] = (salesMap[product.id] || 0) + qty;
      }
    }
  }

  return Object.entries(salesMap)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([productId, quantity]) => {
      const p = products.find(pr => pr.id === productId);
      return {
        productId,
        name: p?.name ?? productId,
        quantity,
        image: p?.images?.[0],
      };
    });
}

// ─── Product Clicks from Supabase ───────────────────────────────────────────────

export async function fetchClicksFromSupabase(): Promise<Record<string, number>> {
  try {
    const { data, error } = await supabase
      .from("flexfit_product_clicks")
      .select("product_id, click_count");

    if (error) {
      console.warn("[store][clicks] fetchClicksFromSupabase:", error);
      return {};
    }

    const result: Record<string, number> = {};
    for (const row of data ?? []) {
      const pid = row.product_id as string;
      const count = (row.click_count as number) ?? 1;
      result[pid] = (result[pid] ?? 0) + count;
    }
    return result;
  } catch (err) {
    console.warn("[store][clicks] fetchClicksFromSupabase exception:", err);
    return {};
  }
}

export async function syncClicksToSupabase(
  clickData: Record<string, number>
): Promise<void> {
  try {
    const rows = Object.entries(clickData).map(([product_id, click_count]) => ({
      // Use anonymous placeholder so the row is insertable
      // (RLS policies on flexfit_product_clicks may allow this)
      user_id: "00000000-0000-0000-0000-000000000000" as string,
      product_id,
      click_count,
      updated_at: new Date().toISOString(),
    }));
    if (rows.length === 0) return;
    await supabase
      .from("flexfit_product_clicks")
      .upsert(rows, { onConflict: "user_id,product_id" });
  } catch (err) {
    console.warn("[store][clicks] syncClicksToSupabase:", err);
  }
}

export async function syncSalesToSupabase(
  productId: string,
  quantity: number
): Promise<void> {
  try {
    const userId = "00000000-0000-0000-0000-000000000000";
    const { error } = await supabase.rpc("increment_sales_count", {
      p_user_id: userId,
      p_product_id: productId,
      p_quantity: quantity,
    });
    if (error) {
      // Fallback: upsert directly
      const { error: upsertErr } = await supabase
        .from("flexfit_product_clicks")
        .upsert(
          { user_id: userId, product_id: productId, sales_count: quantity },
          { onConflict: "user_id,product_id" }
        );
      if (upsertErr) console.warn("[store][sales] syncSalesToSupabase:", upsertErr);
    }
  } catch (err) {
    console.warn("[store][sales] syncSalesToSupabase exception:", err);
  }
}

export async function fetchSalesFromSupabase(): Promise<Record<string, number>> {
  try {
    const { data, error } = await supabase
      .from("flexfit_product_clicks")
      .select("product_id, sales_count");

    if (error) {
      console.warn("[store][sales] fetchSalesFromSupabase:", error);
      return {};
    }

    const result: Record<string, number> = {};
    for (const row of data ?? []) {
      const pid = row.product_id as string;
      const count = (row.sales_count as number) ?? 0;
      if (count > 0) result[pid] = count;
    }
    return result;
  } catch (err) {
    console.warn("[store][sales] fetchSalesFromSupabase exception:", err);
    return {};
  }
}

// Get product click counts — tries Supabase first, falls back to sessionStorage
export async function getProductClickCounts(): Promise<Record<string, number>> {
  const cloud = await fetchClicksFromSupabase();
  if (Object.keys(cloud).length > 0) return cloud;
  try {
    const raw = sessionStorage.getItem("flexfit_product_clicks");
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

// Get top products by clicks (synchronous wrapper — use getProductClickCounts() directly for async)
export function getTopProductsByClicks(): { productId: string; name: string; clicks: number; image?: string }[] {
  // This synchronous version uses sessionStorage only (for backward compat).
  // For Supabase-first data, call fetchClicksFromSupabase() instead.
  const clicks = (() => {
    try {
      const raw = sessionStorage.getItem("flexfit_product_clicks");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  })();
  const products = _products;
  return Object.entries(clicks)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([productId, clickCount]) => {
      const p = products.find(pr => pr.id === productId);
      return {
        productId,
        name: p?.name ?? productId,
        clicks: clickCount,
        image: p?.images?.[0],
      };
    });
}

// Get Top 10 products combining clicks and sales (for dashboard display)
export function getTopProductsAll(): { productId: string; name: string; clicks: number; sales: number; image?: string; rank: number }[] {
  const products = _products;

  // Build click data: prefer Supabase cloud cache, fall back to sessionStorage
  const clicksSource = Object.keys(_cloudClickCounts).length > 0 ? _cloudClickCounts : (() => {
    try {
      const raw = sessionStorage.getItem("flexfit_product_clicks");
      return raw ? (JSON.parse(raw) as Record<string, number>) : {};
    } catch { return {}; }
  })();

  const byClicks = Object.entries(clicksSource)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([productId, clickCount]) => {
      const p = products.find(pr => pr.id === productId);
      return { productId, name: p?.name ?? productId, clicks: clickCount, image: p?.images?.[0] };
    });

  // Sales: prefer Supabase cloud cache, fall back to getTopProductsBySales
  const salesSource = Object.keys(_cloudSalesCounts).length > 0 ? _cloudSalesCounts : (() => {
    const bySales = getTopProductsBySales();
    const map: Record<string, number> = {};
    for (const item of bySales) map[item.productId] = item.quantity;
    return map;
  })();

  const bySales = Object.entries(salesSource)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([productId, quantity]) => {
      const p = products.find(pr => pr.id === productId);
      return { productId, name: p?.name ?? productId, quantity, image: p?.images?.[0] };
    });

  const combinedMap: Record<string, { name: string; clicks: number; sales: number; image?: string }> = {};

  for (const item of byClicks) {
    combinedMap[item.productId] = { name: item.name, clicks: item.clicks, sales: 0, image: item.image };
  }

  for (const item of bySales) {
    if (combinedMap[item.productId]) {
      combinedMap[item.productId].sales = item.quantity;
    } else {
      combinedMap[item.productId] = { name: item.name, clicks: 0, sales: item.quantity, image: item.image };
    }
  }

  const scored = Object.entries(combinedMap)
    .map(([productId, data]) => ({ productId, name: data.name, clicks: data.clicks, sales: data.sales, image: data.image, score: data.clicks + data.sales * 2 }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);

  return scored.map((item, index) => ({
    productId: item.productId,
    name: item.name,
    clicks: item.clicks,
    sales: item.sales,
    image: item.image,
    rank: index + 1,
  }));
}

// Get top categories from real orders
export function getTopCategoriesBySales(): { categoryId: string; name: string; quantity: number }[] {
  const products = _products;
  const categories = _categories;
  const orders = _realOrders;

  // Aggregate sales per category
  const catMap: Record<string, number> = {};
  for (const order of orders) {
    if (!order.itemsSummary) continue;
    const items = order.itemsSummary.split(",").map(s => s.trim());
    for (const item of items) {
      const productName = item.split(" x")[0].trim().toLowerCase();
      if (!productName) continue;

      const product = products.find(p => p.name.toLowerCase().includes(productName) || productName.includes(p.name.toLowerCase()));
      if (product && product.categories?.length) {
        const qtyMatch = item.match(/x(\d+)/);
        const qty = qtyMatch ? parseInt(qtyMatch[1]) : 1;
        for (const catId of product.categories) {
          catMap[catId] = (catMap[catId] || 0) + qty;
        }
      }
    }
  }

  return Object.entries(catMap)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([categoryId, quantity]) => {
      const c = categories.find(cat => cat.id === categoryId);
      return { categoryId, name: c?.label ?? categoryId, quantity };
    });
}

// Get top categories by clicks (aggregate from product clicks)
export function getTopCategoriesByClicks(): { categoryId: string; name: string; clicks: number }[] {
  const productClicks = (() => {
    try {
      const raw = sessionStorage.getItem("flexfit_product_clicks");
      return raw ? (JSON.parse(raw) as Record<string, number>) : {};
    } catch { return {}; }
  })();
  const products = _products;
  const categories = _categories;

  // Aggregate clicks per category
  const catMap: Record<string, number> = {};
  for (const [productId, clicks] of Object.entries(productClicks)) {
    const p = products.find(pr => pr.id === productId);
    if (!p?.categories?.length) continue;
    for (const catId of p.categories) {
      catMap[catId] = (catMap[catId] ?? 0) + clicks;
    }
  }

  return Object.entries(catMap)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([categoryId, clicks]) => {
      const c = categories.find(cat => cat.id === categoryId);
      return { categoryId, name: c?.label ?? categoryId, clicks };
    });
}

export function getFeaturedProduct(period: "week" | "month" | "year"): {
  name: string; clicks: number; sales: number; image?: string;
} {
  const byClicks = getTopProductsByClicks();
  const bySales = getTopProductsBySales();
  const topByClicks = byClicks[0];
  const topBySales = bySales[0];
  if (!topByClicks && !topBySales) {
    return { name: "Aucun produit", clicks: 0, sales: 0 };
  }
  if (period === "week" || period === "month") {
    return {
      name: topByClicks?.name ?? topBySales?.name ?? "Produit top",
      clicks: topByClicks?.clicks ?? 0,
      sales: topBySales?.quantity ?? 0,
      image: topByClicks?.image ?? topBySales?.image,
    };
  }
  // year: best by sales
  return {
    name: topBySales?.name ?? topByClicks?.name ?? "Produit top",
    clicks: topByClicks?.clicks ?? 0,
    sales: topBySales?.quantity ?? 0,
    image: topBySales?.image ?? topByClicks?.image,
  };
}

export function getFeaturedCategory(period: "week" | "month" | "year"): {
  name: string; clicks: number; sales: number;
} {
  const byClicks = getTopCategoriesByClicks();
  const bySales = getTopCategoriesBySales();
  const topByClicks = byClicks[0];
  const topBySales = bySales[0];
  if (!topByClicks && !topBySales) {
    return { name: "Aucune catégorie", clicks: 0, sales: 0 };
  }
  if (period === "week" || period === "month") {
    return {
      name: topByClicks?.name ?? topBySales?.name ?? "Catégorie top",
      clicks: topByClicks?.clicks ?? 0,
      sales: topBySales?.quantity ?? 0,
    };
  }
  return {
    name: topBySales?.name ?? topByClicks?.name ?? "Catégorie top",
    clicks: topByClicks?.clicks ?? 0,
    sales: topBySales?.quantity ?? 0,
  };
}

// ─── Revenue & Transport from daily_stats ───────────────────────────────────

export function getTotalRevenue(): number {
  let total = 0;
  for (const row of _dailyStats) total += Number(row.revenue ?? 0);
  return total;
}

export function getMonthlyRevenue(): number {
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  let total = 0;
  for (const row of _dailyStats) {
    if (row.date_key.startsWith(monthKey)) total += Number(row.revenue ?? 0);
  }
  return total;
}

export function getTotalProfit(): number {
  let total = 0;
  for (const row of _dailyStats) total += Number(row.profit ?? 0);
  return total;
}

export function getMonthlyProfit(): number {
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  let total = 0;
  for (const row of _dailyStats) {
    if (row.date_key.startsWith(monthKey)) total += Number(row.profit ?? 0);
  }
  return total;
}

export function getTransportCostPerBucket(): number {
  return _config?.profitRuleConfig?.transportCostPerBucket ?? 15;
}

export async function getTotalTransportCost(): Promise<number> {
  const transportPerBucket = getTransportCostPerBucket();
  try {
    const { data } = await supabase
      .from("order_items")
      .select("weight_kg_snapshot, quantity");
    if (!data || data.length === 0) return 0;
    let total = 0;
    for (const item of data) {
      const weightKg = Number(item.weight_kg_snapshot ?? 0);
      const qty = Number(item.quantity ?? 1);
      const buckets = Math.ceil(weightKg * qty);
      total += buckets * transportPerBucket;
    }
    return total;
  } catch { return 0; }
}

export async function getMonthlyTransportCost(): Promise<number> {
  const transportPerBucket = getTransportCostPerBucket();
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  try {
    const { data } = await supabase
      .from("order_items")
      .select("weight_kg_snapshot, quantity, created_at")
      .gte("created_at", startOfMonth);
    if (!data || data.length === 0) return 0;
    let total = 0;
    for (const item of data) {
      const weightKg = Number(item.weight_kg_snapshot ?? 0);
      const qty = Number(item.quantity ?? 1);
      const buckets = Math.ceil(weightKg * qty);
      total += buckets * transportPerBucket;
    }
    return total;
  } catch { return 0; }
};

export function getSearchKeywordCounts(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem("flexfit_search_keywords");
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export function getWebsiteVisits(): number {
  return Number(sessionStorage.getItem("flexfit_visits") || "0");
}

export function getVisitStats() {
  const map: Record<string, number> = (() => {
    try {
      const raw = sessionStorage.getItem("flexfit_daily_visits");
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  })();
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  let week = 0;
  const dailyLast7: { day: string; label: string; visits: number }[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const v = map[k] ?? 0;
    if (i === 0) week += v;
    dailyLast7.push({ day: k, label: k.slice(5), visits: v });
  }
  for (let i = 1; i < 7; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    week += map[k] ?? 0;
  }
  const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const month = Object.entries(map).filter(([k]) => k.startsWith(monthPrefix)).reduce((s, [, v]) => s + v, 0);
  return {
    today: map[today] ?? 0,
    week,
    month,
    total: getWebsiteVisits(),
    dailyLast7,
  };
}

export function getTotalUsers(): number {
  try {
    const raw = localStorage.getItem("flexfit_user_profiles");
    if (!raw) return 0;
    const profiles = JSON.parse(raw);
    return Array.isArray(profiles) ? profiles.length : 0;
  } catch { return 0; }
}

export async function getTotalUsersCount(): Promise<number> {
  const CACHE_KEY = "flexfit_admin_total_users_count";
  const CACHE_TS_KEY = "flexfit_admin_total_users_count_ts";
  const TTL_MS = 5 * 60 * 1000; // 5 minutes

  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    const cachedTs = sessionStorage.getItem(CACHE_TS_KEY);
    if (cached && cachedTs && Date.now() - Number(cachedTs) < TTL_MS) {
      return Number(cached);
    }

    const { count, error } = await supabase
      .from("flexfit_user_profiles")
      .select("*", { count: "exact", head: true });
    if (error) {
      console.warn("[store] getTotalUsersCount:", error);
      return cached ? Number(cached) : 0;
    }
    const value = count ?? 0;
    sessionStorage.setItem(CACHE_KEY, String(value));
    sessionStorage.setItem(CACHE_TS_KEY, String(Date.now()));
    return value;
  } catch (err) {
    console.warn("[store] getTotalUsersCount exception:", err);
    const cached = sessionStorage.getItem(CACHE_KEY);
    return cached ? Number(cached) : 0;
  }
}

export function resetAllStats() {
  // Analytics session
  sessionStorage.removeItem("flexfit_visits");
  sessionStorage.removeItem("flexfit_daily_visits");
  sessionStorage.removeItem("flexfit_product_clicks");
  sessionStorage.removeItem("flexfit_search_keywords");
  // Catalog localStorage
  localStorage.removeItem("flexfit_products");
  localStorage.removeItem("flexfit_variants");
  localStorage.removeItem("flexfit_combos");
  // User data localStorage
  localStorage.removeItem("flexfit_favorites");
  localStorage.removeItem("flexfit_user_cart");
  localStorage.removeItem("flexfit_user_orders");
  localStorage.removeItem("flexfit_product_requests");
  notifyStoreUpdate();
}

export async function resetDailyStatsSQL(): Promise<void> {
  try {
    const { error } = await supabase
      .from("flexfit_daily_stats")
      .delete()
      .gt("date_key", "");
    if (error) {
      console.error("[store][SQL] resetDailyStatsSQL failed:", error);
    } else {
      console.log("[store][SQL] Daily stats cleared");
    }
  } catch (err) {
    console.error("[store][SQL] resetDailyStatsSQL exception:", err);
  }
}

/**
 * Reset ALL store data: analytics, products, variants, combos, orders, favorites, cart.
 * Does NOT touch: categories, pricing_rules, affinities, group_cart_tiers,
 * store_config, roles, user_roles, product_images.
 */
export async function resetStoreData(): Promise<void> {
  const tables = [
    // Analytics
    "flexfit_daily_stats",
    "flexfit_daily_visits",
    "flexfit_visits_total",
    "flexfit_product_clicks",
    "flexfit_search_keywords",
    "flexfit_product_requests",
    "flexfit_product_slide_clicks",
    "flexfit_daily_active_users",
    "flexfit_weekly_active_buyers",
    "flexfit_weekly_product_views",
    "flexfit_cart_events",
    "flexfit_product_alerts",
    // User data
    "flexfit_favorites",
    "flexfit_user_cart",
    "flexfit_user_orders",
    // Catalog
    "flexfit_products",
    "flexfit_variants",
    "flexfit_combos",
  ];

  const errors: string[] = [];
  for (const table of tables) {
    try {
      const { error } = await supabase.from(table).delete().neq("id", "");
      if (error) {
        console.warn(`[store][SQL] Reset table ${table}:`, error.message);
        errors.push(`${table}: ${error.message}`);
      } else {
        console.log(`[store][SQL] Cleared table: ${table}`);
      }
    } catch (err) {
      console.warn(`[store][SQL] Reset table ${table} exception:`, err);
      errors.push(`${table}: exception`);
    }
  }

  // Sync in-memory state with now-empty SQL tables
  _products = [];
  _combos = [];
  _realOrders = [];
  _productRequests = [];
  _cloudHydrated = false;
  void loadAllFromSQL();

  if (errors.length === 0) {
    console.log("[store][SQL] All store data reset successfully.");
  } else {
    console.warn("[store][SQL] Some tables could not be reset:", errors);
  }
}

// ─── Product grouping ─────────────────────────────────────────────────────────

export function groupProductsByNameAndCategory(products: Product[]): Product[] {
  const groups = new Map<string, Product[]>();
  for (const product of products) {
    if (!product.name || product.name.trim() === "") continue;
    const key = product.name.trim().toLowerCase();
    const existing = groups.get(key) ?? [];
    existing.push(product);
    groups.set(key, existing);
  }

  const merged: Product[] = [];
  for (const [, group] of groups) {
    if (group.length === 1) { merged.push(group[0]); continue; }
    const base = { ...group[0] };
    const allImages: string[] = [];
    const allImageColors: string[] = [];
    const seenImages = new Set<string>();
    for (const p of group) {
      for (let i = 0; i < p.images.length; i++) {
        const img = p.images[i];
        if (!seenImages.has(img)) {
          seenImages.add(img);
          allImages.push(img);
          allImageColors.push(p.imageColors?.[i] ?? "");
        }
      }
    }
    base.images = allImages;
    base.imageColors = allImageColors.length > 0 ? allImageColors : base.imageColors;
    const allCategories = new Set<string>();
    const allSizes = new Set<string>();
    const allColors = new Set<string>();
    for (const p of group) {
      for (const cat of p.categories) allCategories.add(cat);
      for (const size of p.sizes) allSizes.add(size);
      for (const color of p.colors ?? []) allColors.add(color);
    }
    base.categories = [...allCategories];
    base.sizes = [...allSizes];
    base.colors = [...allColors];
    merged.push(base);
  }
  return merged;
}

// ─── Push all ───────────────────────────────────────────────────────────────

export async function pushAllToSQL(): Promise<void> {
  const products = getProducts();
  const categories = getCategories();
  const combos = getCombos();
  const mergedProducts = groupProductsByNameAndCategory(products);
  console.log(`[store] Pushing ${products.length} raw → ${mergedProducts.length} merged products`);
  await Promise.all([
    pushProductsToSQL(mergedProducts),
    saveCategoriesToSQL(categories),
    pushCombosToSQL(combos),
  ]);
}

// ─── Debug utilities ─────────────────────────────────────────────────────────

/**
 * Teste si les écritures SQL fonctionnent depuis l'admin panel.
 * À appeler depuis la console du navigateur :
 *   import { debugTestSQLWrite } from '@/lib/store';
 *   debugTestSQLWrite();
 */
export async function debugTestSQLWrite(): Promise<void> {
  const testId = `debug-${Date.now()}`;
  console.log("[debug] Testing SQL write to flexfit_products...");

  const { data, error } = await supabase
    .from("flexfit_products")
    .insert({
      id: testId,
      name: "TEST PRODUCT (debug)",
      price: 99,
      categories: [],
      sizes: ["M"],
      colors: [],
      search_keywords: [],
      images: [],
      image_colors: [],
      description: "Debug test product",
    })
    .select()
    .single();

  if (error) {
    console.error("[debug] SQL write FAILED:", error.message);
    console.error("[debug] Details:", error.details);
    console.error("[debug] Hint:", error.hint);
    console.error("[debug] Code:", error.code);
  } else {
    console.log("[debug] SQL write SUCCESS:", data);
    // Cleanup
    await supabase.from("flexfit_products").delete().eq("id", testId);
    console.log("[debug] Test product cleaned up.");
  }
}

/**
 * Teste les écritures sur la table categories.
 */
export async function debugTestCategoriesSQLWrite(): Promise<void> {
  const testId = `debug-cat-${Date.now()}`;
  console.log("[debug] Testing SQL write to categories...");

  const { data, error } = await supabase
    .from("categories")
    .insert({
      id: testId,
      label: "TEST CATEGORY",
      slug: `test-cat-${Date.now()}`,
      order_index: 999,
    })
    .select()
    .single();

  if (error) {
    console.error("[debug] Categories SQL write FAILED:", error.message);
    console.error("[debug] Details:", error.details);
  } else {
    console.log("[debug] Categories SQL write SUCCESS:", data);
    await supabase.from("categories").delete().eq("id", testId);
    console.log("[debug] Test category cleaned up.");
  }
}

/**
 * Affiche un résumé de l'état actuel du store (debug).
 */
export function debugStoreState(): void {
  console.log("=== [debug] Admin Store State ===");
  console.log("Products:", _products.length, "items");
  console.log("Categories:", _categories.length, "items");
  console.log("Combos:", _combos.length, "items");
  console.log("Cloud hydrated:", _cloudHydrated);
}

// ─── Profit Rule Config ─────────────────────────────────────────────────────────

export function defaultProfitRuleConfig(): ProfitRuleConfig {
  return {
    id: "default",
    name: "Regle Congo",
    transportCostPerBucket: 15,
    momoFeeFixed: 2,
    baseSellingPrice: 0,
    greenDiscountMin3: 9,
    greenDiscountUnder3: 7,
    yellowDiscount: 4,
    redDiscount: 1,
    active: true,
  };
}

export function getProfitRuleConfig(): ProfitRuleConfig {
  const stored = localStorage.getItem("flexfit_config");
  if (!stored) return defaultProfitRuleConfig();
  try {
    const config = JSON.parse(stored);
    return config.profitRuleConfig ?? defaultProfitRuleConfig();
  } catch {
    return defaultProfitRuleConfig();
  }
}

export function saveProfitRuleConfig(rule: ProfitRuleConfig): void {
  const stored = localStorage.getItem("flexfit_config");
  let config: Record<string, unknown> = {};
  if (stored) {
    try { config = JSON.parse(stored); } catch { /* use empty */ }
  }
  config.profitRuleConfig = rule;
  localStorage.setItem("flexfit_config", JSON.stringify(config));
  // Also push to SQL (fire-and-forget)
  void saveProfitRuleConfigToSQL(rule);
}

export async function saveProfitRuleConfigToSQL(rule: ProfitRuleConfig): Promise<void> {
  try {
    const { error } = await supabase
      .from("flexfit_profit_rules")
      .upsert({
        id: rule.id,
        name: rule.name,
        transport_cost_per_bucket: rule.transportCostPerBucket,
        momo_fee_fixed: rule.momoFeeFixed,
        base_selling_price: rule.baseSellingPrice,
        green_discount_min3: rule.greenDiscountMin3,
        green_discount_under3: rule.greenDiscountUnder3,
        yellow_discount: rule.yellowDiscount,
        red_discount: rule.redDiscount,
        active: rule.active,
        updated_at: new Date().toISOString(),
      }, { onConflict: "id" });
    if (error) console.warn("[store] saveProfitRuleConfig SQL:", error);
    else console.log("[store] Profit rule saved to SQL");
  } catch (err) {
    console.warn("[store] saveProfitRuleConfig SQL exception:", err);
  }
}

export async function fetchProfitRuleConfigFromSQL(): Promise<ProfitRuleConfig> {
  try {
    const { data } = await supabase
      .from("flexfit_profit_rules")
      .select("*")
      .eq("id", "default")
      .maybeSingle();
    if (!data) return defaultProfitRuleConfig();
    return {
      id: data.id,
      name: data.name,
      transportCostPerBucket: Number(data.transport_cost_per_bucket),
      momoFeeFixed: Number(data.momo_fee_fixed),
      baseSellingPrice: Number(data.base_selling_price ?? 0),
      greenDiscountMin3: Number(data.green_discount_min3),
      greenDiscountUnder3: Number(data.green_discount_under3),
      yellowDiscount: Number(data.yellow_discount),
      redDiscount: Number(data.red_discount),
      active: data.active,
    };
  } catch {
    return defaultProfitRuleConfig();
  }
}

// ─── Order Snapshots ────────────────────────────────────────────────────────────

export function getOrderSnapshots(): import("@/types").OrderSnapshot[] {
  const stored = localStorage.getItem("flexfit_order_history");
  if (!stored) return [];
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// =====================================================================
// MARKETPLACE / VENDORS / CANDIDATURES / PAYOUTS
// =====================================================================
// Toutes ces fonctions interrogent Supabase directement (pas de cache
// localStorage). Chaque mutation appelle notifyStoreUpdate() pour que
// les composants React abonnés rechargent.
// =====================================================================

// ─── Row → Domain mapping ───────────────────────────────────────────────────

interface VendorApplicationRow {
  id: string;
  user_id: string | null;
  full_name: string;
  boutique_name: string;
  email: string;
  phone: string | null;
  country: string | null;
  city: string | null;
  boutique_description: string | null;
  service: string | null;
  status: ApplicationStatus;
  rejection_reason?: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface VendorRow {
  id: string;
  user_id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  status: VendorStatus;
  type: string | null;
  badge: string | null;
  region: string | null;
  country: string | null;
  description: string | null;
  logo_url: string | null;
  banner_url: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

interface BoutiqueRow {
  id: string;
  vendor_id: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  banner_url: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface PayoutRequestRow {
  id: string;
  vendor_id: string;
  order_vendor_split_id: string | null;
  amount: number | null;
  amount_gross: number | null;
  commission_percent: number | null;
  commission_amount: number | null;
  net_amount: number | null;
  payment_method: string | null;
  phone: string | null;
  status: PayoutStatus;
  notes: string | null;
  created_at: string;
  processed_at: string | null;
}

function rowToApplication(row: VendorApplicationRow): VendorApplication {
  return {
    id: row.id,
    userId: row.user_id,
    fullName: row.full_name,
    boutiqueName: row.boutique_name,
    email: row.email,
    phone: row.phone ?? "",
    country: row.country,
    city: row.city,
    description: row.boutique_description ?? null,
    service: row.service,
    status: row.status,
    clarificationMessage: null,
    reviewedBy: row.reviewed_by ?? null,
    reviewedAt: row.reviewed_at ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at ?? row.created_at,
  };
}

function rowToVendor(row: VendorRow): Vendor {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    email: row.email,
    phone: row.phone,
    status: row.status,
    type: (row.type as Vendor["type"]) ?? null,
    badge: (row.badge as Vendor["badge"]) ?? null,
    region: row.region,
    country: row.country,
    description: row.description,
    logoUrl: row.logo_url,
    bannerUrl: row.banner_url,
    approvedAt: row.approved_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function rowToBoutique(row: BoutiqueRow): Boutique {
  return {
    id: row.id,
    vendorId: row.vendor_id,
    name: row.name,
    description: row.description,
    logoUrl: row.logo_url,
    bannerUrl: row.banner_url,
    active: row.active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

interface PayoutSplitInfo {
  id: string;
  user_order_id?: string | null;
  guest_order_id?: string | null;
  boutique_id?: string | null;
  client_name?: string | null;
  client_phone?: string | null;
  client_city?: string | null;
  client_country?: string | null;
  items_summary?: string | null;
  item_count?: number | null;
}

function rowToPayout(
  row: PayoutRequestRow,
  splitMap?: Map<string, PayoutSplitInfo>,
  boutiqueMap?: Map<string, string>,
  vendorMap?: Map<string, { user_id: string | null; phone: string | null; name: string | null }>,
  proofMap?: Map<string, { image_src: string | null; items_summary: string | null }>,
  productImageMap?: Map<string, string | null>
): PayoutRequest {
  const split = row.order_vendor_split_id ? splitMap?.get(row.order_vendor_split_id) : undefined;
  const boutiqueId = split?.boutique_id ?? null;
  const vendor = vendorMap?.get(row.vendor_id);
  const orderRef = split?.user_order_id ?? split?.guest_order_id ?? null;
  const proof = orderRef ? proofMap?.get(orderRef) : undefined;
  const productImage = orderRef ? productImageMap?.get(orderRef) ?? null : null;

  return {
    id: row.id,
    vendorId: row.vendor_id,
    vendorUserId: vendor?.user_id ?? null,
    vendorPhone: vendor?.phone ?? null,
    vendorName: vendor?.name ?? null,
    amount: Number(row.net_amount ?? 0),
    grossAmount: Number(row.amount_gross ?? row.amount ?? 0),
    commissionPercent: row.commission_percent ?? null,
    commissionAmount: row.commission_amount ?? null,
    method: row.payment_method ?? "mobile_money",
    phone: row.phone ?? null,
    status: row.status,
    notes: row.notes,
    createdAt: row.created_at,
    processedAt: row.processed_at,
    orderVendorSplitId: row.order_vendor_split_id ?? null,
    orderRef,
    boutiqueId,
    boutiqueName: boutiqueId ? boutiqueMap?.get(boutiqueId) ?? null : null,
    clientName: split?.client_name ?? null,
    clientPhone: split?.client_phone ?? null,
    clientCity: split?.client_city ?? null,
    clientCountry: split?.client_country ?? null,
    itemsSummary: split?.items_summary ?? proof?.items_summary ?? null,
    itemCount: split?.item_count ?? null,
    paymentProofUrl: proof?.image_src ?? null,
    productImage,
  };
}

// ─── Fetchers (SQL → module state) ───────────────────────────────────────────

export async function fetchVendorsFromSQL(): Promise<Vendor[]> {
  try {
    const { data, error } = await supabase
      .from("vendors")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      markLoadError("fetchVendors", error);
      return [];
    }
    return (data ?? []).map((row: VendorRow) => rowToVendor(row));
  } catch (err) {
    markLoadError("fetchVendors", err);
    return [];
  }
}

export async function fetchVendorApplicationsFromSQL(): Promise<VendorApplication[]> {
  try {
    const { data, error } = await supabase
      .from("vendor_applications")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      markLoadError("fetchVendorApplications", error);
      return [];
    }
    return (data ?? []).map((row: VendorApplicationRow) => rowToApplication(row));
  } catch (err) {
    markLoadError("fetchVendorApplications", err);
    return [];
  }
}

export async function fetchBoutiquesFromSQL(): Promise<Boutique[]> {
  try {
    const { data, error } = await supabase
      .from("boutiques")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      markLoadError("fetchBoutiques", error);
      return [];
    }
    return (data ?? []).map((row: BoutiqueRow) => rowToBoutique(row));
  } catch (err) {
    markLoadError("fetchBoutiques", err);
    return [];
  }
}

/**
 * Extrait le chemin storage (bucket product-images) depuis une URL publique Supabase.
 * Retourne null si l'URL ne pointe pas vers ce bucket.
 */
function storagePathFromUrl(url: string): string | null {
  const marker = "/object/public/product-images/";
  const idx = url.indexOf(marker);
  if (idx === -1) return null;
  return url.slice(idx + marker.length);
}

/**
 * Suppression complète d'une boutique (admin) — IRRÉVERSIBLE.
 * Supprime : produits (+ images DB, embeddings et fichiers storage),
 * services (+ fichiers storage), logo/bannière/dossier storage, puis la boutique.
 * L'historique des commandes est préservé (FK en SET NULL).
 */
export async function deleteBoutiqueFromSQL(boutiqueId: string): Promise<boolean> {
  try {
    // 1. Logo/bannière de la boutique (chemins plats banners/ et logos/)
    const { data: boutique, error: boutiqueErr } = await supabase
      .from("boutiques")
      .select("id, logo_url, banner_url")
      .eq("id", boutiqueId)
      .maybeSingle();
    if (boutiqueErr) {
      console.error("[store][SQL] deleteBoutique fetch:", boutiqueErr);
      return false;
    }
    if (!boutique) {
      console.warn("[store][SQL] deleteBoutique: boutique introuvable", boutiqueId);
      return false;
    }

    // 2. Produits de la boutique : images DB (+ embeddings) + fichiers storage + lignes
    const { data: products, error: prodErr } = await supabase
      .from("flexfit_products")
      .select("id")
      .eq("boutique_id", boutiqueId);
    if (prodErr) {
      console.error("[store][SQL] deleteBoutique fetch products:", prodErr);
      return false;
    }
    for (const p of products ?? []) {
      await deleteProductImages(p.id);
      await deleteProductStorageImages(p.id);
      await deleteProductFromSQL(p.id);
    }

    // 3. Services : fichiers storage (chemins plats retrouvés via les URLs)
    const { data: services } = await supabase
      .from("flexfit_services")
      .select("images, hero_image_url")
      .eq("boutique_id", boutiqueId);
    const servicePaths = (services ?? [])
      .flatMap((s: { images: string[] | null; hero_image_url: string | null }) => [
        ...(s.images ?? []),
        s.hero_image_url,
      ])
      .filter((u): u is string => typeof u === "string" && u.length > 0)
      .map(storagePathFromUrl)
      .filter((p): p is string => !!p);
    if (servicePaths.length > 0) {
      await supabase.storage.from("product-images").remove(servicePaths);
      console.log("[store][storage] Deleted", servicePaths.length, "service files");
    }

    // 4. Dossier storage boutiques/<id>/ + logo/bannière
    const { data: folderFiles } = await supabase.storage
      .from("product-images")
      .list(`boutiques/${boutiqueId}`);
    const folderPaths = (folderFiles ?? [])
      .filter((f) => f.name)
      .map((f) => `boutiques/${boutiqueId}/${f.name}`);
    const brandPaths = [boutique.logo_url, boutique.banner_url]
      .filter((u): u is string => typeof u === "string" && u.length > 0)
      .map(storagePathFromUrl)
      .filter((p): p is string => !!p);
    const allPaths = [...new Set([...folderPaths, ...brandPaths])];
    if (allPaths.length > 0) {
      await supabase.storage.from("product-images").remove(allPaths);
      console.log("[store][storage] Deleted", allPaths.length, "boutique files");
    }

    // 5. Suppression de la boutique (CASCADE : services, reviews, follows, etc.)
    const { error: delErr } = await supabase
      .from("boutiques")
      .delete()
      .eq("id", boutiqueId);
    if (delErr) {
      console.error("[store][SQL] deleteBoutique delete:", delErr);
      return false;
    }

    _boutiques = _boutiques.filter((b) => b.id !== boutiqueId);
    console.log("[store][SQL] Deleted boutique:", boutiqueId);
    return true;
  } catch (err) {
    console.error("[store][SQL] deleteBoutique exception:", err);
    return false;
  }
}

/**
 * Masque / affiche une boutique (toggle `active`).
 * Une boutique inactive est invisible publiquement, ainsi que tous ses produits
 * (policy "Public read flexfit_products"). Le vendor et les admins la voient toujours.
 */
export async function updateBoutiqueActiveSQL(boutiqueId: string, active: boolean): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("boutiques")
      .update({ active, updated_at: new Date().toISOString() })
      .eq("id", boutiqueId);
    if (error) {
      console.error("[store][SQL] updateBoutiqueActive:", error);
      return false;
    }
    _boutiques = _boutiques.map((b) => (b.id === boutiqueId ? { ...b, active } : b));
    console.log("[store][SQL] Boutique", boutiqueId, active ? "visible" : "masquée");
    return true;
  } catch (err) {
    console.error("[store][SQL] updateBoutiqueActive exception:", err);
    return false;
  }
}

/**
 * Masque / affiche un produit individuel (toggle `is_hidden`).
 * Un produit masqué est invisible publiquement (policy "Public read
 * flexfit_products") même si sa boutique est active. Le vendor et les
 * admins le voient toujours.
 */
export async function updateProductHiddenSQL(productId: string, hidden: boolean): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("flexfit_products")
      .update({ is_hidden: hidden, updated_at: new Date().toISOString() })
      .eq("id", productId);
    if (error) {
      console.error("[store][SQL] updateProductHidden:", error);
      return false;
    }
    _products = _products.map((p) => (p.id === productId ? { ...p, isHidden: hidden } : p));
    console.log("[store][SQL] Produit", productId, hidden ? "masqué" : "visible");
    return true;
  } catch (err) {
    console.error("[store][SQL] updateProductHidden exception:", err);
    return false;
  }
}

export async function fetchPayoutRequestsFromSQL(): Promise<PayoutRequest[]> {
  try {
    const { data, error } = await supabase
      .from("flexfit_payout_records")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      markLoadError("fetchPayoutRequests", error);
      return [];
    }
    const payouts = (data ?? []) as PayoutRequestRow[];
    console.log("[store][SQL] fetchPayoutRequests: raw payouts count =", payouts.length);

    // Bulk-fetch related vendors, splits, boutiques and payment proofs to enrich payouts
    const vendorIds = [...new Set(payouts.map((p) => p.vendor_id).filter(Boolean))] as string[];
    const vendorMap = new Map<string, { user_id: string | null; phone: string | null; name: string | null }>();
    if (vendorIds.length > 0) {
      const { data: vendors } = await supabase
        .from("vendors")
        .select("id, user_id, phone, name")
        .in("id", vendorIds);
      for (const v of vendors ?? []) {
        vendorMap.set(v.id, { user_id: v.user_id, phone: v.phone, name: v.name });
      }
    }

    const splitIds = payouts
      .map((p) => p.order_vendor_split_id)
      .filter((id): id is string => Boolean(id));
    const splitMap = new Map<string, PayoutSplitInfo>();
    const boutiqueMap = new Map<string, string>();
    const orderIds = new Set<string>();
    let splits: PayoutSplitInfo[] = [];

    if (splitIds.length > 0) {
      const uniqueSplitIds = [...new Set(splitIds)];
      const { data: splitsData, error: splitsError } = await supabase
        .from("flexfit_order_vendor_split")
        .select(
          "id, user_order_id, guest_order_id, boutique_id, client_name, client_phone, client_city, client_country, items_summary, item_count"
        )
        .in("id", uniqueSplitIds);

      if (splitsError) {
        console.error("[store][SQL] fetchPayoutRequests splits error:", splitsError);
      }

      splits = (splitsData ?? []) as PayoutSplitInfo[];
      for (const s of splits) {
        splitMap.set(s.id, s);
        if (s.user_order_id) orderIds.add(s.user_order_id);
        if (s.guest_order_id) orderIds.add(s.guest_order_id);
      }

      const boutiqueIds = [...new Set(splits.map((s) => s.boutique_id).filter(Boolean))] as string[];
      if (boutiqueIds.length > 0) {
        const { data: boutiques } = await supabase
          .from("boutiques")
          .select("id, name")
          .in("id", boutiqueIds);
        for (const b of boutiques ?? []) {
          boutiqueMap.set(b.id, b.name);
        }
      }
    }

    // Fetch payment proofs by order id (user_order_id or guest_order_id)
    const proofMap = new Map<string, { image_src: string | null; items_summary: string | null }>();
    const productImageMap = new Map<string, string | null>();
    const orderIdsArray = [...orderIds];

    if (orderIdsArray.length > 0) {
      const { data: proofs } = await supabase
        .from("flexfit_payment_proofs")
        .select("order_id, image_src, items_summary")
        .in("order_id", orderIdsArray);
      for (const p of proofs ?? []) {
        if (p.order_id) {
          proofMap.set(p.order_id, { image_src: p.image_src, items_summary: p.items_summary });
        }
      }

      // Fetch first product image for each order (user orders)
      const userOrderIds = orderIdsArray.filter((id) =>
        splits?.some((s) => s.user_order_id === id)
      );
      if (userOrderIds.length > 0) {
        const { data: userItems } = await supabase
          .from("order_items")
          .select("order_id, product_snapshot")
          .in("order_id", userOrderIds)
          .order("created_at", { ascending: true });
        for (const item of userItems ?? []) {
          if (!productImageMap.has(item.order_id)) {
            const image = (item.product_snapshot as any)?.image ?? null;
            if (image) productImageMap.set(item.order_id, image);
          }
        }
      }

      // Fetch first product image for each order (guest orders)
      const guestOrderIds = orderIdsArray.filter((id) =>
        splits?.some((s) => s.guest_order_id === id)
      );
      if (guestOrderIds.length > 0) {
        const { data: guestItems } = await supabase
          .from("flexfit_guest_order_items")
          .select("guest_order_id, product_image")
          .in("guest_order_id", guestOrderIds)
          .order("created_at", { ascending: true });
        for (const item of guestItems ?? []) {
          if (!productImageMap.has(item.guest_order_id)) {
            if (item.product_image) productImageMap.set(item.guest_order_id, item.product_image);
          }
        }
      }
    }

    const enriched = payouts.map((row) => rowToPayout(row, splitMap, boutiqueMap, vendorMap, proofMap, productImageMap));
    console.log("[store][SQL] fetchPayoutRequests: enriched count =", enriched.length);
    return enriched;
  } catch (err) {
    // flexfit_payout_records may not exist or may be empty
    console.warn("[store][SQL] fetchPayoutRequests: table likely missing", err);
    return [];
  }
}

// ─── Getters (module state, synced via fetchVendorsFromSQL) ─────────────────

export function getVendors(): Vendor[] {
  return _vendors;
}

export function getVendorApplications(): VendorApplication[] {
  return _vendorApplications;
}

export function getBoutiques(): Boutique[] {
  return _boutiques;
}

export function getPayoutRequests(): PayoutRequest[] {
  return _payoutRequests;
}

// ─── Stats: vendors enrichis avec compteurs ────────────────────────────────

/**
 * Récupère les vendors enrichis avec le nombre de produits, commandes et
 * boutiques. Utilise la vue `vendor_stats` (créée par la migration) si
 * disponible, sinon fallback sur 3 requêtes parallèles.
 */
export async function getVendorsWithStats(): Promise<VendorWithStats[]> {
  // Tentative via vue d'abord
  try {
    const { data, error } = await supabase
      .from("vendor_stats")
      .select("*");
    if (!error && data && data.length > 0) {
      return data.map((row: VendorRow & {
        product_count?: number;
        order_count?: number;
        boutique_count?: number;
      }) => ({
        ...rowToVendor(row),
        productCount: Number(row.product_count ?? 0),
        orderCount: Number(row.order_count ?? 0),
        boutiqueCount: Number(row.boutique_count ?? 0),
      }));
    }
  } catch {
    // Vue pas encore créée → fallback
  }

  // Fallback : vendors + compteurs en parallèle
  try {
    const [vendors, products, orders, boutiques] = await Promise.all([
      fetchVendorsFromSQL(),
      supabase.from("flexfit_products").select("id, boutique_id"),
      supabase.from("order_items").select("product_id"),
      supabase.from("boutiques").select("id, vendor_id"),
    ]);

    // Build boutique_id → vendor_id index
    const boutiqueVendorIndex = new Map<string, string>();
    (boutiques.data ?? []).forEach((b: { id: string; vendor_id: string }) => {
      boutiqueVendorIndex.set(b.id, b.vendor_id);
    });

    // Build product → boutique index
    const productBoutiqueIndex = new Map<string, string>();
    (products.data ?? []).forEach((p: { id: string; boutique_id: string | null }) => {
      if (p.boutique_id) productBoutiqueIndex.set(p.id, p.boutique_id);
    });

    // Count products per vendor
    const productsByVendor = new Map<string, number>();
    productBoutiqueIndex.forEach((boutiqueId) => {
      const vendorId = boutiqueVendorIndex.get(boutiqueId);
      if (vendorId) {
        productsByVendor.set(vendorId, (productsByVendor.get(vendorId) ?? 0) + 1);
      }
    });

    // Count orders per vendor (orders linked via product → boutique → vendor)
    const ordersByVendor = new Map<string, Set<string>>();
    (orders.data ?? []).forEach((oi: { product_id: string }) => {
      const boutiqueId = productBoutiqueIndex.get(oi.product_id);
      if (!boutiqueId) return;
      const vendorId = boutiqueVendorIndex.get(boutiqueId);
      if (!vendorId) return;
      if (!ordersByVendor.has(vendorId)) ordersByVendor.set(vendorId, new Set());
      ordersByVendor.get(vendorId)!.add(oi.product_id);
    });

    // Count boutiques per vendor
    const boutiquesByVendor = new Map<string, number>();
    (boutiques.data ?? []).forEach((b: { vendor_id: string }) => {
      boutiquesByVendor.set(b.vendor_id, (boutiquesByVendor.get(b.vendor_id) ?? 0) + 1);
    });

    return vendors.map((v) => ({
      ...v,
      productCount: productsByVendor.get(v.id) ?? 0,
      orderCount: ordersByVendor.get(v.id)?.size ?? 0,
      boutiqueCount: boutiquesByVendor.get(v.id) ?? 0,
    }));
  } catch (err) {
    console.error("[store][SQL] getVendorsWithStats exception:", err);
    return [];
  }
}

// ─── Mutations: applications ────────────────────────────────────────────────

/**
 * Approuve une candidature : crée vendor + boutique + update candidature
 * en deux étapes (pas de RPC needed).
 * Retourne l'ID du vendor créé, ou null en cas d'erreur.
 */
export async function approveVendorApplicationSQL(
  applicationId: string
): Promise<{ vendorId: string; password: string } | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user.id;

    if (!adminId) {
      console.error("[store] approveVendorApplication: no session");
      toast.error("Vous devez être connecté pour approuver une candidature.");
      return null;
    }

    // 1. Récupérer la candidature
    const { data: app, error: appError } = await supabase
      .from("vendor_applications")
      .select("*")
      .eq("id", applicationId)
      .single();

    if (appError || !app) {
      console.error("[store] approveVendorApplication: could not fetch app", appError);
      toast.error("Candidature introuvable.");
      return null;
    }

    const now = new Date().toISOString();
    const vendorId = crypto.randomUUID();
    const boutiqueId = crypto.randomUUID();

    // 2. Mettre à jour la candidature
    const { error: updateError } = await supabase
      .from("vendor_applications")
      .update({
        status: "approved",
        reviewed_by: adminId,
        reviewed_at: now,
      })
      .eq("id", applicationId);

    if (updateError) {
      console.error("[store] approveVendorApplication: update failed", updateError);
      toast.error(updateError.message ?? "Erreur lors de l'approbation.");
      return null;
    }

    // 3. Créer le vendor — user_id résolu en priorité via candidature,
    //    sinon via match email sur auth.users via RPC find_auth_user_by_email
    let resolvedUserId: string | null = app.user_id;
    if (!resolvedUserId && app.email) {
      try {
        const { data: authMatch } = await supabase.rpc("find_auth_user_by_email", {
          p_email: app.email,
        });
        resolvedUserId = (authMatch as { id: string }[] | null)?.[0]?.id ?? null;
      } catch (e) {
        console.warn("[store] find_auth_user_by_email lookup failed:", e);
      }
    }

    // Déclaré ICI, pas dans le try de l'Edge Function — sinon ReferenceError
    // "vendorUserId is not defined" à l'étape du message de bienvenue.
    let vendorUserId: string | null = resolvedUserId;

    // Insert idempotent : vendors.user_id est UNIQUE, donc un retry après un
    // échec partiel (vendor déjà créé) renvoyait un 409 et bloquait tout.
    // On réutilise alors la fiche existante.
    let effectiveVendorId = vendorId;
    const { error: vendorError } = await supabase
      .from("vendors")
      .insert({
        id: vendorId,
        user_id: resolvedUserId,
        email: app.email,
        name: app.boutique_name,
        phone: app.phone,
        status: "approved",
        type: app.service || 'produit',
        region: app.city,
        country: app.country ?? "RDC",
        description: app.boutique_description,
        badge: "marchand_approuve",
      });

    if (vendorError) {
      // Conflit probable (vendor déjà créé pour ce compte/email) → on récupère
      // la fiche existante et on la répare au lieu d'échouer.
      let existing: { id: string; user_id: string | null } | null = null;
      if (resolvedUserId) {
        const { data } = await supabase
          .from("vendors")
          .select("id, user_id")
          .eq("user_id", resolvedUserId)
          .neq("status", "deleted")
          .limit(1)
          .maybeSingle();
        existing = data;
      }
      if (!existing && app.email) {
        const { data } = await supabase
          .from("vendors")
          .select("id, user_id")
          .eq("email", app.email)
          .neq("status", "deleted")
          .limit(1)
          .maybeSingle();
        existing = data;
      }

      if (!existing) {
        console.error("[store] approveVendorApplication: vendor insert failed", vendorError);
        toast.error(vendorError.message ?? "Erreur lors de la création du vendor.");
        return null;
      }

      effectiveVendorId = existing.id;
      if (existing.user_id) vendorUserId = existing.user_id;
      await supabase
        .from("vendors")
        .update({
          status: "approved",
          user_id: existing.user_id ?? resolvedUserId,
          name: app.boutique_name,
          phone: app.phone,
        })
        .eq("id", effectiveVendorId);
    }

    // 3.5 Creer le compte auth vendeur via Edge Function
    let vendorPassword = "";
    try {
      // L'edge function exige un JWT admin (audit sécurité 2026-07-25)
      const { data: { session } } = await supabase.auth.getSession();
      const edgeRes = await fetch("https://pwyfevvozzmmffzpbfsp.supabase.co/functions/v1/setup-vendor-account", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { "Authorization": `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ vendor_id: effectiveVendorId, email: app.email, boutique_name: app.boutique_name }),
      });
      const edgeData = await edgeRes.json() as { password?: string; user_id?: string; errors?: string[] };
      vendorPassword = edgeData.password ?? "";
      if (edgeData.user_id) {
        vendorUserId = edgeData.user_id;
        await supabase.from("vendors").update({ user_id: edgeData.user_id }).eq("id", effectiveVendorId).catch(() => {});
      }
    } catch (e) {
      console.warn("[store] Edge Function call failed:", e);
    }

    // 4. Assigner le rôle vendor dans user_roles — sur le compte auth RÉEL
    //    (créé par l'Edge Function si la candidature n'avait pas de user_id).
    if (vendorUserId) {
      const { error: roleError } = await supabase
        .from("user_roles")
        .upsert(
          { user_id: vendorUserId, role: "vendor" },
          { onConflict: "user_id,role" }
        );

      if (roleError) {
        console.error("[store] approveVendorApplication: user_roles insert failed", roleError);
      }
    }

    // 5. Créer la boutique (idempotent : une seule boutique par vendor ici)
    const { data: existingBoutique } = await supabase
      .from("boutiques")
      .select("id")
      .eq("vendor_id", effectiveVendorId)
      .limit(1)
      .maybeSingle();

    if (!existingBoutique) {
      const { error: boutiqueError } = await supabase
        .from("boutiques")
        .insert({
          id: boutiqueId,
          vendor_id: effectiveVendorId,
          name: app.boutique_name,
          logo_url: null,
          banner_url: null,
          active: true,
        });

      if (boutiqueError) {
        console.error("[store] approveVendorApplication: boutique insert failed", boutiqueError);
        toast.error(boutiqueError.message ?? "Erreur lors de la création de la boutique.");
        return null;
      }
    }

    // 5.5 Envoyer le message de bienvenue dans le chat
    const chatUserId = app.user_id ?? vendorUserId;
    if (chatUserId) {
      // Si un mot de passe provisoire a été généré (compte inexistant), il est
      // envoyé par email ; sinon le vendeur garde SON mot de passe.
      const loginHint = vendorPassword
        ? "\u{1F4E7} Tes identifiants de connexion ont été envoyés sur " + app.email + "\n\n"
        : "\u{1F511} Connecte-toi avec le mot de passe de ton compte Pamoya.\n\n";
      const welcomeText = "\u{1F389} Felicitations ! Ta boutique **" + app.boutique_name + "** est approuvee et en ligne sur Pamoya.\n\n" +
        loginHint +
        "\u{1F517} Espace vendeur : https://pamoya.org/seller/login\n\n" +
        "\u{1F4AC} Besoin d aide ? Reponds a ce message.\n— L equipe Pamoya \u{1F9E1}";
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const adminId = session?.user.id;
        if (adminId) {
          const { data: conv } = await supabase.from("flexfit_chat_conversations").insert({
            client_id: chatUserId,
            vendor_id: vendorUserId ?? chatUserId,
            boutique_id: null,
            boutique_name: app.boutique_name,
            type: "support",
            last_message: welcomeText.slice(0, 200),
            last_message_at: new Date().toISOString(),
            status: "active",
          }).select("id").single();

          if (conv?.id) {
            await supabase.from("flexfit_chat_messages").insert({
              conversation_id: conv.id,
              sender_id: adminId,
              sender_role: "admin",
              content: welcomeText,
              created_at: new Date().toISOString(),
            });
          }
        }
      } catch (e) {
        console.warn("[store] Auto chat welcome failed (non-blocking):", e);
      }
    }

    toast.success("Candidature approuvée !");
    await refreshMarketplaceFromSQL();
    return { vendorId: effectiveVendorId, password: vendorPassword };
  } catch (err) {
    console.error("[store] approveVendorApplication exception:", err);
    toast.error("Erreur lors de l'approbation.");
    return null;
  }
}

export async function rejectVendorApplicationSQL(
  applicationId: string,
  reason?: string
): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user.id;

    if (!adminId) {
      toast.error("Vous devez être connecté.");
      return false;
    }

    const { error } = await supabase
      .from("vendor_applications")
      .update({
        status: "rejected",
        reviewed_by: adminId,
        reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        rejection_reason: reason ?? null,
      })
      .eq("id", applicationId);

    if (error) {
      console.error("[store] rejectVendorApplication:", error);
      toast.error(error.message ?? "Erreur lors du rejet.");
      return false;
    }

    // Update local cache
    _vendorApplications = _vendorApplications.map((a) =>
      a.id === applicationId
        ? { ...a, status: "rejected", reviewedBy: adminId, reviewedAt: new Date().toISOString() }
        : a
    );
    notifyStoreUpdate();
    return true;
  } catch (err) {
    console.error("[store] rejectVendorApplication exception:", err);
    toast.error("Erreur lors du rejet.");
    return false;
  }
}

export async function requestClarificationSQL(
  applicationId: string,
  message: string
): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user.id;

    if (!adminId) {
      toast.error("Vous devez être connecté.");
      return false;
    }

    if (!message.trim()) {
      toast.error("Le message ne peut pas être vide.");
      return false;
    }

    const { error } = await supabase
      .from("vendor_applications")
      .update({
        status: "clarification",
        boutique_description: message.trim(),
        reviewed_by: adminId,
        reviewed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", applicationId);

    if (error) {
      console.error("[store] requestClarification:", error);
      toast.error(error.message ?? "Erreur lors de l'envoi.");
      return false;
    }

    _vendorApplications = _vendorApplications.map((a) =>
      a.id === applicationId
        ? { ...a, status: "clarification", clarificationMessage: message.trim(), reviewedBy: adminId, reviewedAt: new Date().toISOString() }
        : a
    );
    notifyStoreUpdate();
    return true;
  } catch (err) {
    console.error("[store] requestClarification exception:", err);
    toast.error("Erreur lors de l'envoi.");
    return false;
  }
}

/**
 * Met à jour le statut d'une candidature (utilisé pour les modifs en bulk
 * ou les transitions manuelles hors flux standard).
 */
export async function updateApplicationStatusSQL(
  applicationId: string,
  status: ApplicationStatus,
  extras?: { clarificationMessage?: string; reviewedBy?: string }
): Promise<boolean> {
  try {
    const update: Record<string, unknown> = {
      status,
      updated_at: new Date().toISOString(),
    };
    if (extras?.clarificationMessage !== undefined) {
      // Store clarification message in the description field for now
      // (no dedicated column exists in the current schema)
      update.boutique_description = extras.clarificationMessage;
    }
    if (extras?.reviewedBy !== undefined) {
      // reviewed_by column does not exist in current schema — store in boutique_description for traceability
      update.boutique_description = `[Reviewer: ${extras.reviewedBy}] ${extras.clarificationMessage ?? ""}`.trim();
      if (!extras.clarificationMessage) delete update.boutique_description;
    }
    if (status === "approved" || status === "rejected") {
      update.reviewed_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from("vendor_applications")
      .update(update)
      .eq("id", applicationId);

    if (error) {
      console.error("[store] updateApplicationStatus:", error);
      toast.error(error.message ?? "Erreur lors de la mise à jour.");
      return false;
    }

    _vendorApplications = _vendorApplications.map((a) =>
      a.id === applicationId ? { ...a, status, ...extras } : a
    );
    notifyStoreUpdate();
    return true;
  } catch (err) {
    console.error("[store] updateApplicationStatus exception:", err);
    return false;
  }
}

// ─── Mutations: vendors ─────────────────────────────────────────────────────

export async function updateVendorStatusSQL(
  vendorId: string,
  status: VendorStatus
): Promise<boolean> {
  try {
    const update: Record<string, unknown> = {
      status,
      updated_at: new Date().toISOString(),
    };
    if (status === "active" && !_vendors.find((v) => v.id === vendorId)?.approvedAt) {
      update.approved_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from("vendors")
      .update(update)
      .eq("id", vendorId);

    if (error) {
      console.error("[store] updateVendorStatus:", error);
      toast.error(error.message ?? "Erreur lors de la mise à jour du vendor.");
      return false;
    }

    _vendors = _vendors.map((v) =>
      v.id === vendorId
        ? { ...v, status, updatedAt: new Date().toISOString(), ...(status === "active" ? { approvedAt: v.approvedAt ?? new Date().toISOString() } : {}) }
        : v
    );
    notifyStoreUpdate();
    return true;
  } catch (err) {
    console.error("[store] updateVendorStatus exception:", err);
    return false;
  }
}

export async function updateVendorBadgeSQL(
  vendorId: string,
  badge: Vendor["badge"]
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("vendors")
      .update({ badge, updated_at: new Date().toISOString() })
      .eq("id", vendorId);

    if (error) {
      console.error("[store] updateVendorBadge:", error);
      toast.error(error.message ?? "Erreur lors de la mise à jour du badge.");
      return false;
    }

    _vendors = _vendors.map((v) =>
      v.id === vendorId ? { ...v, badge, updatedAt: new Date().toISOString() } : v
    );
    notifyStoreUpdate();
    return true;
  } catch (err) {
    console.error("[store] updateVendorBadge exception:", err);
    return false;
  }
}

export async function updateVendorCountrySQL(
  vendorId: string,
  country: string
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("vendors")
      .update({ country, updated_at: new Date().toISOString() })
      .eq("id", vendorId);

    if (error) {
      console.error("[store] updateVendorCountry:", error);
      toast.error(error.message ?? "Erreur lors de la mise à jour du pays.");
      return false;
    }

    _vendors = _vendors.map((v) =>
      v.id === vendorId ? { ...v, country, updatedAt: new Date().toISOString() } : v
    );
    notifyStoreUpdate();
    return true;
  } catch (err) {
    console.error("[store] updateVendorCountry exception:", err);
    return false;
  }
}

export async function deleteVendorSQL(vendorId: string): Promise<boolean> {
  // Soft delete : passe le statut à "deleted" pour préserver l'historique
  return updateVendorStatusSQL(vendorId, "deleted");
}

/**
 * Purge complète d'un vendor (IRRÉVERSIBLE) :
 * 1. order_items de ses boutiques (historique commandes)
 * 2. commandes guest mono-vendor (CASCADE : guest_order_items + splits)
 * 3. chaque boutique via deleteBoutiqueFromSQL (produits, images DB+storage, services)
 * 4. le vendor (CASCADE : splits multi-vendor restants, payouts, boutiques restantes)
 */
export async function purgeVendorSQL(vendorId: string): Promise<boolean> {
  try {
    // 0. Boutiques du vendor
    const { data: boutiques, error: bErr } = await supabase
      .from("boutiques")
      .select("id")
      .eq("vendor_id", vendorId);
    if (bErr) {
      console.error("[store][SQL] purgeVendor fetch boutiques:", bErr);
      return false;
    }
    const boutiqueIds = (boutiques ?? []).map((b: { id: string }) => b.id);

    // 1. Lignes de commande liées à ces boutiques
    if (boutiqueIds.length > 0) {
      const { error: oiErr } = await supabase
        .from("order_items")
        .delete()
        .in("boutique_id", boutiqueIds);
      if (oiErr) {
        console.error("[store][SQL] purgeVendor order_items:", oiErr);
        return false;
      }
    }

    // 2. Commandes guest mono-vendor (CASCADE : guest_order_items + splits).
    //    Les commandes multi-vendors sont conservées pour les autres vendeurs ;
    //    les splits de ce vendor tomberont en CASCADE à l'étape 4.
    if (!(await deleteMonoVendorGuestOrders(vendorId))) return false;

    // 3. Boutiques (produits + images + storage + services)
    for (const boutiqueId of boutiqueIds) {
      const ok = await deleteBoutiqueFromSQL(boutiqueId);
      if (!ok) {
        console.error("[store][SQL] purgeVendor: échec suppression boutique", boutiqueId);
        return false;
      }
    }

    // 4. Vendor (CASCADE : splits restants, payouts, boutiques restantes)
    const { error: vErr } = await supabase
      .from("vendors")
      .delete()
      .eq("id", vendorId);
    if (vErr) {
      console.error("[store][SQL] purgeVendor delete vendor:", vErr);
      return false;
    }

    _vendors = _vendors.filter((v) => v.id !== vendorId);
    notifyStoreUpdate();
    console.log("[store][SQL] Vendor purgé complètement:", vendorId);
    return true;
  } catch (err) {
    console.error("[store][SQL] purgeVendor exception:", err);
    return false;
  }
}

/**
 * Supprime les commandes guest dont TOUS les splits appartiennent à ce vendor.
 * Les commandes multi-vendors sont conservées pour les autres vendeurs.
 */
async function deleteMonoVendorGuestOrders(vendorId: string): Promise<boolean> {
  const { data: splits, error: sErr } = await supabase
    .from("flexfit_order_vendor_split")
    .select("guest_order_id, vendor_id")
    .not("guest_order_id", "is", null);
  if (sErr) {
    console.error("[store][SQL] deleteMonoVendorGuestOrders fetch splits:", sErr);
    return false;
  }
  const vendorsByOrder = new Map<string, Set<string>>();
  for (const s of splits ?? []) {
    if (!s.guest_order_id) continue;
    if (!vendorsByOrder.has(s.guest_order_id)) vendorsByOrder.set(s.guest_order_id, new Set());
    vendorsByOrder.get(s.guest_order_id)!.add(s.vendor_id);
  }
  const monoVendorOrderIds = [...vendorsByOrder.entries()]
    .filter(([, vendorIds]) => vendorIds.size === 1 && vendorIds.has(vendorId))
    .map(([orderId]) => orderId);
  if (monoVendorOrderIds.length === 0) return true;
  const { error: goErr } = await supabase
    .from("flexfit_guest_orders")
    .delete()
    .in("id", monoVendorOrderIds);
  if (goErr) {
    console.error("[store][SQL] deleteMonoVendorGuestOrders delete:", goErr);
    return false;
  }
  console.log("[store][SQL]", monoVendorOrderIds.length, "commande(s) guest mono-vendor supprimée(s)");
  return true;
}

/**
 * Réinitialise l'historique commandes d'un vendor (ex. commandes de test) :
 * order_items de ses boutiques + commandes guest mono-vendor + tous ses splits.
 * Les boutiques, produits, images et le vendor sont conservés.
 */
export async function resetVendorHistorySQL(vendorId: string): Promise<boolean> {
  try {
    const { data: boutiques, error: bErr } = await supabase
      .from("boutiques")
      .select("id")
      .eq("vendor_id", vendorId);
    if (bErr) {
      console.error("[store][SQL] resetVendorHistory fetch boutiques:", bErr);
      return false;
    }
    const boutiqueIds = (boutiques ?? []).map((b: { id: string }) => b.id);

    // Lignes de commande liées à ses boutiques
    if (boutiqueIds.length > 0) {
      const { error: oiErr } = await supabase
        .from("order_items")
        .delete()
        .in("boutique_id", boutiqueIds);
      if (oiErr) {
        console.error("[store][SQL] resetVendorHistory order_items:", oiErr);
        return false;
      }
    }

    // Commandes guest 100% à lui
    if (!(await deleteMonoVendorGuestOrders(vendorId))) return false;

    // Tous ses splits (y compris sa part des commandes multi-vendors)
    const { error: spErr } = await supabase
      .from("flexfit_order_vendor_split")
      .delete()
      .eq("vendor_id", vendorId);
    if (spErr) {
      console.error("[store][SQL] resetVendorHistory splits:", spErr);
      return false;
    }

    notifyStoreUpdate();
    console.log("[store][SQL] Historique vendor réinitialisé:", vendorId);
    return true;
  } catch (err) {
    console.error("[store][SQL] resetVendorHistory exception:", err);
    return false;
  }
}

// ─── Mutations: payouts ─────────────────────────────────────────────────────

export async function updatePayoutStatusSQL(
  payoutId: string,
  status: PayoutStatus
): Promise<boolean> {
  try {
    const update: Record<string, unknown> = { status };
    const now = new Date().toISOString();
    if (status === "paid" || status === "rejected") {
      update.processed_at = now;
    }

    const { error } = await supabase
      .from("flexfit_payout_records")
      .update(update)
      .eq("id", payoutId);

    if (error) {
      console.error("[store] updatePayoutStatus:", error);
      toast.error(error.message ?? "Erreur lors de la mise à jour du payout.");
      return false;
    }

    _payoutRequests = _payoutRequests.map((p) =>
      p.id === payoutId
        ? { ...p, status, processedAt: (status === "paid" || status === "rejected") ? now : p.processedAt }
        : p
    );
    notifyStoreUpdate();
    return true;
  } catch (err) {
    console.error("[store] updatePayoutStatus exception:", err);
    return false;
  }
}

// ─── Direct vendor creation (admin shortcut) ───────────────────────────────

/**
 * Crée un vendor directement sans passer par une candidature.
 * Insère directement en base: vendor + boutique (pas de RPC).
 */
export async function createVendorDirectlySQL(input: {
  fullName: string;
  boutiqueName: string;
  email: string;
  phone: string | null;
  city: string | null;
  service: string;
  country: string;
}): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user.id;

    if (!adminId) {
      toast.error("Vous devez être connecté.");
      return false;
    }

    if (!input.fullName.trim() || !input.boutiqueName.trim() || !input.email.trim()) {
      toast.error("Nom, boutique et email sont requis.");
      return false;
    }

    const vendorId = crypto.randomUUID();
    const boutiqueId = crypto.randomUUID();
    const now = new Date().toISOString();

    // 1. Créer le vendor
    const { error: vendorError } = await supabase
      .from("vendors")
      .insert({
        id: vendorId,
        user_id: null,
        name: input.boutiqueName,
        email: input.email,
        phone: input.phone,
        status: "active",
        type: input.service as string ?? null,
        region: input.city,
        country: input.country || "RDC",
        description: null,
        logo_url: null,
        banner_url: null,
        badge: "marchand_approuve",
        approved_at: now,
        created_at: now,
        updated_at: now,
      });

    if (vendorError) {
      console.error("[store] createVendorDirectly insert vendor:", vendorError);
      toast.error(vendorError.message ?? "Erreur lors de la création du vendor.");
      return false;
    }

    // 2. Créer la boutique liée
    const { error: boutiqueError } = await supabase
      .from("boutiques")
      .insert({
        id: boutiqueId,
        vendor_id: vendorId,
        name: input.boutiqueName,
        description: null,
        logo_url: null,
        banner_url: null,
        active: true,
        created_at: now,
        updated_at: now,
      });

    if (boutiqueError) {
      console.error("[store] createVendorDirectly insert boutique:", boutiqueError);
      // Rollback vendor
      await supabase.from("vendors").delete().eq("id", vendorId);
      toast.error(boutiqueError.message ?? "Erreur lors de la création de la boutique.");
      return false;
    }

    toast.success("Vendor créé avec succès !");
    await refreshMarketplaceFromSQL();
    return true;
  } catch (err) {
    console.error("[store] createVendorDirectly exception:", err);
    toast.error("Erreur lors de la création du vendor.");
    return false;
  }
}

// ─── Refresh marketplace (sync module state) ───────────────────────────────

export async function refreshMarketplaceFromSQL(): Promise<void> {
  // Efface un éventuel flag d'erreur sur les scopes refetchés ici : les fetchers
  // le re-marqueront si l'échec persiste. Sans ça, un retry réussi laissait
  // l'état d'erreur affiché dans les pages.
  const scopes = ["fetchVendors", "fetchVendorApplications", "fetchBoutiques", "fetchPayoutRequests"];
  if (_lastLoadError && scopes.some((s) => _lastLoadError!.startsWith(s))) {
    _lastLoadError = null;
  }
  try {
    const [vendors, applications, boutiques, payouts] = await Promise.all([
      fetchVendorsFromSQL(),
      fetchVendorApplicationsFromSQL(),
      fetchBoutiquesFromSQL(),
      fetchPayoutRequestsFromSQL(),
    ]);

    _vendors = vendors;
    _vendorApplications = applications;
    _boutiques = boutiques;
    _payoutRequests = payouts;
    notifyStoreUpdate();
  } catch (err) {
    console.error("[store] refreshMarketplace exception:", err);
  }
}

// ─── Counters (pour badges sidebar) ────────────────────────────────────────

/**
 * Nombre de candidatures en attente. Lit depuis le cache module-level
 * (rapide, pas de requête SQL).
 */
export function getPendingApplicationsCount(): number {
  return _vendorApplications.filter((a) => a.status === "pending").length;
}

export function getActiveVendorsCount(): number {
  return _vendors.filter((v) => v.status === "active").length;
}

export function getPendingPayoutsCount(): number {
  return _payoutRequests.filter((p) => p.status === "pending" || p.status === "calculated").length;
}

/**
 * Nombre de demandes de produits non traitées (status = "new").
 * Alimente la carte "Demandes produits" du Dashboard.
 */
export function getPendingProductRequestsCount(): number {
  return _productRequests.filter((r) => r.status === "new").length;
}

/**
 * Nombre de commandes en attente de validation (status = "pending" ou "en attente").
 * Alimente la carte "Commandes à valider" du Dashboard.
 */
export function getOrdersToValidateCount(): number {
  return _realOrders.filter((o) => {
    const s = (o.status ?? "").toLowerCase();
    return s === "pending" || s === "en attente" || s === "awaiting";
  }).length;
}

// ─── Marketplace dashboard helpers ─────────────────────────────────────────

/**
 * Nombre de nouveaux vendors inscrits depuis le début du mois en cours.
 * Utilisé pour afficher le delta "nouveau ce mois" dans le KPI Vendeurs actifs.
 */
export function getNewVendorsThisMonth(): number {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  return _vendors.filter((v) => {
    if (!v.createdAt) return false;
    const t = new Date(v.createdAt).getTime();
    return !Number.isNaN(t) && t >= monthStart;
  }).length;
}

/**
 * Top N vendors les plus actifs, classés par nombre de commandes payées.
 * Pour marketplace dashboard : "Top vendeurs" remplace "Top performers produits".
 */
export function getTopVendors(
  limit = 5,
  period: "week" | "month" | "year" = "month",
): { vendorId: string; name: string; orderCount: number; avatarUrl?: string | null }[] {
  const now = new Date();
  let since: Date;
  if (period === "week") {
    since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  } else if (period === "year") {
    since = new Date(now.getFullYear(), 0, 1);
  } else {
    since = new Date(now.getFullYear(), now.getMonth(), 1);
  }
  const sinceMs = since.getTime();

  // Index vendorId → vendor
  const vendorIndex = new Map<string, Vendor>();
  _vendors.forEach((v) => vendorIndex.set(v.id, v));

  // Aggregate order counts by vendor
  const counts = new Map<string, number>();
  for (const order of _realOrders) {
    const created = new Date(order.createdAt ?? "").getTime();
    if (!created || created < sinceMs) continue;
    // vendorSplits (vendor → boutique → orders)
    const splits = (order as RealOrder).vendorSplits ?? [];
    for (const s of splits) {
      if (!s?.vendorId) continue;
      counts.set(s.vendorId, (counts.get(s.vendorId) ?? 0) + 1);
    }
  }

  return Array.from(counts.entries())
    .map(([vendorId, orderCount]) => {
      const v = vendorIndex.get(vendorId);
      return {
        vendorId,
        name: v?.name ?? "Vendeur",
        orderCount,
        avatarUrl: v?.logoUrl ?? null,
      };
    })
    .sort((a, b) => b.orderCount - a.orderCount)
    .slice(0, limit);
}

/**
 * Derniers N vendors inscrits (toutes plateformes confondues).
 * Utilisé pour la mini-liste "Nouveaux vendeurs" du dashboard.
 */
export function getRecentVendors(limit = 5): Vendor[] {
  return [..._vendors]
    .filter((v) => v.createdAt)
    .sort((a, b) => {
      const ta = new Date(a.createdAt).getTime();
      const tb = new Date(b.createdAt).getTime();
      return tb - ta;
    })
    .slice(0, limit);
}

/**
 * Commissions totales perçues par la plateforme (somme des commissionAmount
 * sur les commandes payées). C'est le vrai revenu de la marketplace.
 */
export function getTotalCommission(): number {
  let total = 0;
  for (const order of _realOrders) {
    if (!isPaidOrder(order)) continue;
    const splits = order.vendorSplits ?? [];
    for (const s of splits) {
      total += Number(s?.commissionAmount ?? 0);
    }
  }
  return total;
}

/**
 * Commissions perçues ce mois (commandes payées depuis le 1er du mois).
 */
export function getMonthlyCommission(): number {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  let total = 0;
  for (const order of _realOrders) {
    const created = new Date(order.createdAt ?? "").getTime();
    if (!created || created < monthStart) continue;
    if (!isPaidOrder(order)) continue;
    const splits = order.vendorSplits ?? [];
    for (const s of splits) {
      total += Number(s?.commissionAmount ?? 0);
    }
  }
  return total;
}

/**
 * Compte les commandes payées (statut "paid" / "payee" / "Payée").
 */
export function getPaidOrdersCount(): number {
  return _realOrders.filter(isPaidOrder).length;
}

function isPaidOrder(order: RealOrder): boolean {
  const s = (order.status ?? "").toString().toLowerCase().trim();
  return s === "paid" || s === "payée" || s === "payee" || s === "paye";
}
